import React, { ReactNode, useCallback, useEffect, useState, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import {
  FaEye,
  FaEyeSlash,
  FaExternalLinkAlt,
  FaChevronLeft,
  FaChevronRight,
  FaPrint,
  FaLock,
  FaMoneyBillWave,
  FaEject,
  FaCrown,
  FaDesktop,
  FaTrash
} from 'react-icons/fa'
import { LanguageSelector } from '@renderer/components'
import DiskCleanupModal, { formatGB, useDiskCleanupStatus } from './DiskCleanupModal'
import { useSessionStateStore } from '@renderer/stores/sessionState'
import { useSettingsStore } from '@renderer/stores/settingsStore'
import { parseCaptureFormat, resolveCaptureFormat } from '@renderer/utils/captureEncoding'
import { usePrintStore, type RemotePrinterEntry } from '@renderer/stores/printStore'
import { applyRecommendationToStoreIfStock } from '@renderer/utils/printerRecommendations'
import {
  savedPrinterIsMissingFromList,
  shouldForgetSavedPrinter
} from '@renderer/utils/savedPrinter'
import {
  useAutoUpdate,
  useFullscreenManager,
  useSessionLayout,
  type DeviceSettings
} from '@renderer/hooks'
import cogoToast from '@successtar/cogo-toast'
import { unifiedCameraUtils } from '@renderer/utils/cameras/uniCamUtils'
import {
  findUsbCameraBySelection,
  makeUsbSelectionValueForDevice,
  disambiguateWebcamLabels,
  disambiguateUsbCameraLabels,
  usbCameraSerialSuffix
} from '@renderer/utils/cameras/cameraSelection'
import { cameraDriverStatusKey } from '../../../../shared/cameraDriverState'
import { CameraGuideModal } from './CameraGuideModal'
import { PrinterGuideModal } from './PrinterGuideModal'
import { PrinterSettingsModal } from './PrinterSettingsModal'
import { PaperRollRow } from './PaperRollRow'
import { isBahela } from '@renderer/utils/brandUtils'
import { RemotePrintSettingsModal } from './RemotePrintSettingsModal'
import { CameraPrompt } from './CameraPrompt'
import {
  SettingsNav,
  SETTINGS_GROUPS,
  type SettingsGroupId,
  type SettingsGroupStatus
} from './SettingsNav'
import PinModal from '../PinModal'
import { Button } from '@renderer/components'
import { useParams } from 'react-router-dom'
import { useFirstLoadStore } from '@renderer/stores/firstLoadStore'
import CameraSettingsModal from './CameraSettingsModal'
import { useCreditsStore } from '@renderer/stores/creditsStore'
import { useConfigStore } from '@renderer/stores/configState'
import { paymentRailFor } from '@renderer/utils/paymentRail'
import {
  connectedTerminalProvider,
  isConnectedTerminalAvailable,
  type ConnectedTerminalProvider
} from '@renderer/utils/pointPayment'
import ConnectedTerminalPicker from '@renderer/components/view/ConnectedTerminalPicker'
import { NayaxTerminalField, useNayaxAccount } from '@renderer/components/view/NayaxTerminalField'
import { UpgradeModal } from '../UpgradeModal'
import { useSubscriptionStore } from '@renderer/stores/subscriptionStore'
import { useOfflineStore } from '@renderer/stores/offlineStore'
import { useSubscriptionCache } from '@renderer/hooks/useSubscriptionCache'
import { useAuthStore } from '@renderer/stores/authStore'
import { useCameraStore } from '@renderer/stores/cameraStore'
import { useBoothEventStore } from '@renderer/stores/boothEventStore'
import { useFullscreen } from '@renderer/utils'
import {
  capabilities,
  getDiskCleanupApi,
  getHostAppVersion,
  getPreloadApi,
  hasElectronPreload,
  INSTALL_REFUSED_BUSY,
  isInTauriShell,
  openExternalUrl,
} from '@renderer/lib/runtime'
import {
  refreshRemotePrinterRegistry,
  remoteMachineDisplayLabel
} from '@renderer/utils/remotePrinterRegistry'

interface SettingsModalProps {
  setShowSettings: (show: boolean, fromGear?: boolean) => void
  isFromGearButton?: boolean
  height: number
  devices?: DeviceSettings
  fetchDevices: () => Promise<void>
  cameraConnectionRef: React.RefObject<{
    id: string
    connected: boolean
    isPreviewAvailable: boolean
  }>
  isPreviewAvailable: boolean
  setIsPreviewAvailable: (available: boolean) => void
  localTempCamera: string
  setLocalTempCamera: (id: string) => void
  /**
   * Section to land on when the modal opens. Set by callers that open settings
   * BECAUSE something is wrong (camera failed, camera missing at startup), so the
   * operator arrives at the fix. The gear passes nothing and lands at the top.
   */
  initialSection?: SettingsGroupId
}

export const SettingsModal: React.FC<SettingsModalProps> = ({
  setShowSettings,
  isFromGearButton = false,
  devices,
  fetchDevices,
  cameraConnectionRef,
  isPreviewAvailable,
  setIsPreviewAvailable,
  localTempCamera,
  setLocalTempCamera,
  initialSection
}) => {
  const { t } = useTranslation()
  const { config } = useConfigStore()
  const { primaryColor } = config?.theme || {
    primaryColor: '#3B82F6'
  }

  // The rail this project's guests actually pay with. The Payments section
  // names it rather than saying QRIS at an operator whose region has never
  // had a QRIS code on screen.
  const paymentRail = paymentRailFor(config)
  const paymentRailLabel =
    paymentRail === 'qris'
      ? t('settings.payments.railQris', 'QRIS')
      : t('settings.payments.railOnline', 'the QR code')

  // Configuration: Set to false to disable app update notifications
  const ENABLE_UPDATE_NOTIFICATIONS = false
  const [showPassword, setShowPassword] = useState(false)
  const [showGuideModal, setShowGuideModal] = useState(false)
  const [showPrinterGuideModal, setShowPrinterGuideModal] = useState(false)
  const [showPrinterSettings, setShowPrinterSettings] = useState(false)
  const [showRemotePrintSettings, setShowRemotePrintSettings] = useState(false)
  const [showChangePinModal, setShowChangePinModal] = useState(false)
  const [showCameraSettings, setShowCameraSettings] = useState(false)
  const [activeGroup, setActiveGroup] = useState<SettingsGroupId>(initialSection ?? 'camera')
  const [downloadDestination, setDownloadDestination] = useState<string | undefined>()
  const [showDiskCleanup, setShowDiskCleanup] = useState(false)
  const { status: diskStatus, refresh: refreshDiskStatus } = useDiskCleanupStatus(activeGroup === 'files')
  const { projectId } = useParams()
  const { getPageSettings } = useConfigStore()
  const welcomeSettings = getPageSettings('welcome')
  const customPath = welcomeSettings?.customPath
  const isEditCustomPath = customPath === 'edit'
  const {
    selectedPrinter,
    password,
    setPassword,
    downloadLocal,
    setDownloadLocal,
    autoStart,
    setAutoStart,
    autoLaunch,
    setAutoLaunch,
    setDefaultProjectId,
    setSelectedCamera,
    selectedCamera,
    debugFps,
    setDebugFps,
    deviceId,
    enableScreensaver,
    screensaverTimeout,
    setSetting,
    suppressBrowserGestures,
    setSuppressBrowserGestures,
    cardTerminal,
    setCardTerminal
  } = useSettingsStore()
  // A connected terminal is a per-acquirer integration. Mercado Pago Point is
  // Mexico only, so it opens up on MXN projects; a Nayax reader opens up
  // wherever the operator has a Nayax account in Studio, which is only asked
  // once the Payments section is open. A booth already paired always sees it.
  const nayaxAccount = useNayaxAccount(activeGroup === 'payments')
  const connectedTerminalAvailable = isConnectedTerminalAvailable(
    config?.resolvedCurrency,
    cardTerminal.terminalId,
    nayaxAccount.kind === 'ready'
  )
  const pointTerminalPossible =
    String(config?.resolvedCurrency ?? '').toUpperCase() === 'MXN' ||
    (connectedTerminalProvider(cardTerminal) === 'mercadopago' && Boolean(cardTerminal.terminalId))
  const nayaxTerminalPossible =
    nayaxAccount.kind === 'ready' || cardTerminal.provider === 'nayax'
  // Which integration the connected-terminal panel shows. Unset (a booth
  // never paired) follows what this project can actually use.
  const connectedPanelProvider: ConnectedTerminalProvider =
    cardTerminal.provider ?? (pointTerminalPossible || !nayaxTerminalPossible ? 'mercadopago' : 'nayax')
  const chooseConnectedProvider = (provider: ConnectedTerminalProvider): void => {
    if (provider === connectedPanelProvider) return
    // A Point id means nothing to Nayax and the other way round.
    setCardTerminal({ provider, terminalId: '' })
  }
  const { setSessionState, userId, projectId: sessionProjectId } = useSessionStateStore()
  const { user: authUser } = useAuthStore()
  const {
    setSelectedPrinter,
    isPrintingEnabled,
    togglePrinting,
    remotePrinters,
    selectedPrinterTarget,
    selectedRemotePrinterMachineId,
    selectedRemotePrinterName,
    selectRemotePrinter,
    selectDefaultPrinter
  } = usePrintStore()
  const { setIsFirstLoad } = useFirstLoadStore()
  const { credits, fetchSubscriptionAndCredits } = useCreditsStore()
  const { activationCode } = useSettingsStore()
  const { type: packageType, isValid, proTrialExpiresAt } = useSubscriptionStore()
  const hasDeviceSubscription = Boolean(activationCode && isValid)
  const displayType = hasDeviceSubscription ? packageType : 'free'
  const [remainingTime, setRemainingTime] = useState<string | null>(null)
  const isKlakKlikKluk = window.location.hostname.includes('klakklikkluk')
  const { appVersion, setAppVersion } = useOfflineStore()
  const { toggleFullscreen } = useFullscreen()

  // Initialize subscription cache
  useSubscriptionCache()

  // Auto-updater. `state` stays null outside Electron and on brands that ship no
  // update feed (bahela, klakklikkluk) — the section renders nothing there.
  const autoUpdate = useAutoUpdate()

  const { isFullscreen } = useFullscreenManager()
  const { isCompact: isViewportCompact, isPortraitViewport } = useSessionLayout()
  const useCompactModal = capabilities.isMobile || isViewportCompact
  /** Physical phone portrait: smaller type + aligned padding with the rest of the modal */
  const settingsPortraitCompact = useCompactModal && isPortraitViewport

  const [localTempPrinter, setLocalTempPrinter] = useState<string>('')
  const [pinModalTitle, setPinModalTitle] = useState<string>('Change PIN')
  const [pinModalSubtitle, setPinModalSubtitle] = useState<string | ReactNode>(
    'Enter 6-digit PIN to continue'
  )

  const [showCameraPrompt, setShowCameraPrompt] = useState(false)
  const [showUpgradeModal, setShowUpgradeModal] = useState(false)
  const [pendingCameraConnection, setPendingCameraConnection] = useState<{
    type: 'detect' | 'usb'
    params: {
      productId?: number
      vendorId?: number
      serialNumber?: string
    }
    /** The dropdown value that initiated this connection (persisted on success). */
    selectionValue?: string
  } | null>(null)

  const [fetchedProjectTitle, setProjectTitle] = useState<string>('')

  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const [localAppVersion, setLocalAppVersion] = useState<string | undefined>(undefined)
  const [latestAppVersion, setLatestAppVersion] = useState<string | undefined>(undefined)
  const [showUpdateBubble, setShowUpdateBubble] = useState<boolean>(false)
  const [hasConnectionError, setHasConnectionError] = useState<boolean>(false)

  // Privilege restrictions state (simplified - only detects Store installation)
  const [privilegeRestrictions, setPrivilegeRestrictions] = useState<{
    isStoreInstall: boolean
  } | null>(null)

  // Monitor pinning: which display the booth window sits on. Only meaningful in
  // Electron with more than one monitor attached.
  const [displays, setDisplays] = useState<
    Array<{
      id: number
      label: string
      width: number
      height: number
      scaleFactor: number
      isPrimary: boolean
      isCurrent: boolean
    }>
  >([])
  const [isMovingDisplay, setIsMovingDisplay] = useState(false)

  // Windows system controls state
  const [sysControlsStatus, setSysControlsStatus] = useState<{
    edgeSwipe: string
    lockOnWake: { AC: string; DC: string }
    timeouts: {
      VIDEO_AC: string
      VIDEO_DC: string
      SLEEP_AC: string
      SLEEP_DC: string
      HAS_BATTERY: 'True' | 'False'
    }
  } | null>(null)
  const [sysControlsLoading, setSysControlsLoading] = useState<{
    edgeSwipe: boolean
    lockOnWakeDisable: boolean
    lockOnWakeEnable: boolean
    timeouts: boolean
  }>({
    edgeSwipe: false,
    lockOnWakeDisable: false,
    lockOnWakeEnable: false,
    timeouts: false
  })

  // Common toggle switch styles
  const toggleSwitchClasses = `w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all`

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return (): void => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  // Fetch devices every 5 seconds, but stop when unmounted
  useEffect(() => {
    setDefaultProjectId(projectId)

    // Trigger log upload silently in background when settings modal is opened
    const api = getPreloadApi()
    if (api?.logs?.uploadNow) {
      api.logs.uploadNow().catch((err) => console.log('Silent log upload skipped/failed:', err))
    }
  }, [])

  // Booth telemetry: one coalesced settings_session per open→close. Re-opens
  // within 60s merge (count++). Emitted on unmount via the store's debounce.
  useEffect(() => {
    if (projectId) useBoothEventStore.getState().setProjectContext(projectId)
    useBoothEventStore.getState().beginSettingsSession()
    return (): void => {
      useBoothEventStore.getState().endSettingsSession()
    }
  }, [])

  // Which settings group the operator is actually looking at. Fires on mount too,
  // so `tabsTouched` records the landing group rather than only later navigation.
  useEffect(() => {
    useBoothEventStore.getState().markSettingsTab(activeGroup)
  }, [activeGroup])

  // Record which settings areas the operator actually opened (for tabsTouched).
  useEffect(() => {
    const mark = useBoothEventStore.getState().markSettingsTab
    if (showCameraSettings) mark('camera')
    if (showPrinterSettings) mark('printer')
    if (showRemotePrintSettings) mark('remotePrint')
    if (showChangePinModal) mark('pin')
    if (showGuideModal) mark('cameraGuide')
    if (showPrinterGuideModal) mark('printerGuide')
  }, [
    showCameraSettings,
    showPrinterSettings,
    showRemotePrintSettings,
    showChangePinModal,
    showGuideModal,
    showPrinterGuideModal
  ])

  // ProTrial countdown logic (only when device subscription is active)
  useEffect(() => {
    let timer: NodeJS.Timeout | null = null
    if (hasDeviceSubscription && displayType?.toLowerCase() === 'protrial' && proTrialExpiresAt) {
      const expiryDate = new Date(proTrialExpiresAt)

      const updateCountdown = (): void => {
        const now = new Date()
        const diff = expiryDate.getTime() - now.getTime()

        if (diff <= 0) {
          setRemainingTime(t('proTrial.expired'))
          if (timer) clearInterval(timer)
          return
        }

        const days = Math.floor(diff / (1000 * 60 * 60 * 24))
        const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))
        const seconds = Math.floor((diff % (1000 * 60)) / 1000)

        if (days === 0) {
          setRemainingTime(t('proTrial.timeRemainingRealtime', { hours, minutes, seconds }))
        } else {
          setRemainingTime(t('proTrial.timeRemainingDays', { days }))
        }
      }

      updateCountdown()
      timer = setInterval(updateCountdown, 1000)
    } else {
      setRemainingTime(null)
    }

    return (): void => {
      if (timer) clearInterval(timer)
    }
  }, [hasDeviceSubscription, displayType, proTrialExpiresAt, t])

  useEffect(() => {
    setTimeout(() => {
      if (selectedCamera) {
        handleCameraChange(selectedCamera)
      }
      // Only forget a saved printer against a list we actually have: an empty
      // list means "not enumerated yet", not "your printer is gone".
      // See utils/savedPrinter.ts.
      if (shouldForgetSavedPrinter(selectedPrinter, devices?.printers)) {
        setSelectedPrinter(undefined)
        setLocalTempPrinter('')
      } else if (selectedPrinter) {
        setLocalTempPrinter(selectedPrinter)
      }
    }, 1000)
  }, [devices, isFromGearButton])

  useEffect(() => {
    if (selectedPrinter) {
      setLocalTempPrinter(selectedPrinter)
    }
  }, [selectedPrinter])

  // Discover remote printers (any running booth registered for this user) and
  // feed the dropdown. Same util PrintUtils re-resolves with at print time.
  useEffect(() => {
    void refreshRemotePrinterRegistry()
  }, [])

  useEffect(() => {
    const fetchProjectDetails = async (): Promise<void> => {
      if (!projectId) return

      try {
        const response = await fetch(`${process.env.VITE_API_URL}/api/projects/${projectId}`, {
          credentials: 'include'
        })
        const data = await response.json()
        setProjectTitle(data.title)
      } catch (error) {
        console.error('Error fetching project details:', error)
      }
    }

    fetchProjectDetails()
    if (!isKlakKlikKluk) {
      fetchSubscriptionAndCredits()
    }

    const fetchAppVersion = async (): Promise<void> => {
      let version: string | null | undefined
      const v = await getHostAppVersion()
      if (v) {
        version = v
        setLocalAppVersion(version)
        setAppVersion(version)
      }

      try {
        const response = await fetch(`${process.env.VITE_API_URL}/api/releases`)
        if (response.ok) {
          const data = await response.json()
          const latestVersion = data[0].version.replace('v', '')
          setLatestAppVersion(latestVersion)

          if (version && latestVersion) {
            const currentParts = version.split('.').map(Number)
            const latestParts = latestVersion.split('.').map(Number)

            let needsUpdate = false
            for (let i = 0; i < Math.max(currentParts.length, latestParts.length); i++) {
              const current = currentParts[i] || 0
              const latest = latestParts[i] || 0

              if (latest > current) {
                needsUpdate = true
                break
              } else if (latest < current) {
                needsUpdate = false
                break
              }
            }
            setShowUpdateBubble(needsUpdate)
          } else if (latestVersion) {
            setShowUpdateBubble(true)
          }
        }
      } catch (error) {
        console.error('Error fetching latest release:', error)
      }
    }
    fetchAppVersion()

    const fetchDownloadPath = async (): Promise<void> => {
      const dlApi = getPreloadApi()
      if (
        hasElectronPreload() &&
        dlApi &&
        'getDownloadDestination' in dlApi &&
        typeof dlApi.getDownloadDestination === 'function'
      ) {
        try {
          const dest = await dlApi.getDownloadDestination()
          setDownloadDestination(dest || undefined)
        } catch (error) {
          console.error('Failed to get download destination:', error)
        }
      }
    }
    fetchDownloadPath()
  }, [projectId])

  const handleCameraChange = async (
    newCameraId: string,
    event?: MouseEvent | TouchEvent | Event
  ): Promise<void> => {
    const isClick = event?.isTrusted || isFromGearButton // Consider gear button click as trusted
    try {
      if (newCameraId === 'detect') {
        if (!isClick) {
          setShowCameraPrompt(true)
          setPendingCameraConnection({
            type: 'detect',
            params: {}
          })
          return
        }

        const result = await unifiedCameraUtils.tryToConnect('camera', {}, 'primary')
        const healthCheck = await unifiedCameraUtils.isDeviceResponding('primary')
        if (!healthCheck.isResponding) {
          await unifiedCameraUtils.disconnect('primary')
          throw new Error('Camera connected but not responding')
        }
        setIsPreviewAvailable(true)
        setLocalTempCamera(newCameraId)

        const deviceName = result.deviceInfo
          ? `${result.deviceInfo.manufacturerName || ''} ${result.deviceInfo.productName || ''}`.trim() ||
          'USB Camera'
          : 'USB Camera'

        setSessionState({
          devices: {
            camera: {
              id: newCameraId,
              name: deviceName,
              status: 'ready',
              type: 'camera'
            }
          }
        })

        setSelectedCamera(newCameraId)

        // Increment connection key to force Preview remount
        const { incrementCameraConnectionKey } = useSettingsStore.getState()
        incrementCameraConnectionKey()

        if (cameraConnectionRef?.current) {
          Object.assign(cameraConnectionRef.current, {
            id: newCameraId,
            connected: true,
            isPreviewAvailable: true
          })
        }
        return
      }
    } catch (err) {
      const errorMessage = (err as Error).message
      cogoToast.error('Failed to detect camera')
      cogoToast.error(errorMessage)
      setHasConnectionError(true)
      setIsFirstLoad(false)
      setIsPreviewAvailable(false)
      setLocalTempCamera('')
    }

    setLocalTempCamera(newCameraId)
    setIsPreviewAvailable(false)

    if (cameraConnectionRef?.current) {
      Object.assign(cameraConnectionRef.current, {
        id: '',
        connected: false,
        isPreviewAvailable: false
      })
    }

    if (!newCameraId) {
      return
    }

    try {
      // Find the camera device from either webcams or USB cameras
      const webcam = devices?.cameras.webcams.find((w) => w.deviceId === newCameraId)
      const usbCamera = findUsbCameraBySelection(devices?.cameras.usbCameras, newCameraId)

      if (usbCamera) {
        if (!usbCamera.hasDriver) {
          setShowGuideModal(true)
          return
        }

        const usbParams = {
          productId: usbCamera.productId,
          vendorId: usbCamera.vendorId,
          serialNumber: usbCamera.serialNumber
        }

        if (!isClick) {
          setShowCameraPrompt(true)
          setPendingCameraConnection({
            type: 'usb',
            params: usbParams,
            selectionValue: newCameraId
          })
          return
        }

        await unifiedCameraUtils.tryToConnect('camera', usbParams, 'primary')
        const healthCheckUsb = await unifiedCameraUtils.isDeviceResponding('primary')
        if (!healthCheckUsb.isResponding) {
          await unifiedCameraUtils.disconnect('primary')
          throw new Error('Camera connected but not responding')
        }
        setIsPreviewAvailable(true)
        setLocalTempCamera(newCameraId)
        setSessionState({
          devices: {
            camera: {
              id: newCameraId,
              name: usbCamera.brand || 'USB Camera',
              status: 'ready',
              type: 'camera'
            }
          }
        })

        setSelectedCamera(newCameraId)

        // Increment connection key to force Preview remount
        const { incrementCameraConnectionKey } = useSettingsStore.getState()
        incrementCameraConnectionKey()

        if (cameraConnectionRef?.current) {
          Object.assign(cameraConnectionRef.current, {
            id: newCameraId,
            connected: true,
            isPreviewAvailable: true
          })
        }
      } else if (webcam) {
        await unifiedCameraUtils.tryToConnect('webcam', newCameraId, 'primary')
        const healthCheckWebcam = await unifiedCameraUtils.isDeviceResponding('primary')
        if (!healthCheckWebcam.isResponding) {
          await unifiedCameraUtils.disconnect('primary')
          throw new Error('Camera connected but not responding')
        }
        setIsPreviewAvailable(true)
        setLocalTempCamera(newCameraId)
        setSessionState({
          devices: {
            camera: {
              id: newCameraId,
              name: webcam.label || 'Webcam',
              status: 'ready',
              type: 'webcam'
            }
          }
        })

        setSelectedCamera(newCameraId)

        // Increment connection key to force Preview remount
        const { incrementCameraConnectionKey } = useSettingsStore.getState()
        incrementCameraConnectionKey()

        if (cameraConnectionRef?.current) {
          Object.assign(cameraConnectionRef.current, {
            id: newCameraId,
            connected: true,
            isPreviewAvailable: true
          })
        }
      }
    } catch (err) {
      const errorMessage = (err as Error).message
      await unifiedCameraUtils.disconnect()
      cogoToast.error(errorMessage || 'Failed to change camera')
      setHasConnectionError(true)
      setIsFirstLoad(false)
      setIsPreviewAvailable(false)
      setLocalTempCamera('')
    }
  }

  const handleCameraPromptStart = async (): Promise<void> => {
    if (!pendingCameraConnection) return

    try {
      if (pendingCameraConnection.type === 'detect') {
        const result = await unifiedCameraUtils.tryToConnect('camera', {}, 'primary')
        const healthCheckDetect = await unifiedCameraUtils.isDeviceResponding('primary')
        if (!healthCheckDetect.isResponding) {
          await unifiedCameraUtils.disconnect('primary')
          throw new Error('Camera connected but not responding')
        }
        setIsPreviewAvailable(true)
        setLocalTempCamera('detect')

        const deviceName = result.deviceInfo
          ? `${result.deviceInfo.manufacturerName || ''} ${result.deviceInfo.productName || ''}`.trim() ||
          'USB Camera'
          : 'USB Camera'

        setSessionState({
          devices: {
            camera: {
              id: 'detect',
              name: deviceName,
              status: 'ready',
              type: 'camera'
            }
          }
        })

        setSelectedCamera('detect')

        // Increment connection key to force Preview remount
        const { incrementCameraConnectionKey } = useSettingsStore.getState()
        incrementCameraConnectionKey()

        if (cameraConnectionRef?.current) {
          Object.assign(cameraConnectionRef.current, {
            id: 'detect',
            connected: true,
            isPreviewAvailable: true
          })
        }
      } else {
        await unifiedCameraUtils.tryToConnect('camera', pendingCameraConnection.params, 'primary')
        const healthCheckUsbPrompt = await unifiedCameraUtils.isDeviceResponding('primary')
        if (!healthCheckUsbPrompt.isResponding) {
          await unifiedCameraUtils.disconnect('primary')
          throw new Error('Camera connected but not responding')
        }
        setIsPreviewAvailable(true)
        const newCameraId =
          pendingCameraConnection.selectionValue ?? `${pendingCameraConnection.params.productId}`
        setLocalTempCamera(newCameraId)
        setSessionState({
          devices: {
            camera: {
              id: newCameraId,
              name: 'USB Camera',
              status: 'ready',
              type: 'camera'
            }
          }
        })

        setSelectedCamera(newCameraId)

        // Increment connection key to force Preview remount
        const { incrementCameraConnectionKey } = useSettingsStore.getState()
        incrementCameraConnectionKey()

        if (cameraConnectionRef?.current) {
          Object.assign(cameraConnectionRef.current, {
            id: newCameraId,
            connected: true,
            isPreviewAvailable: true
          })
        }
      }
    } catch (err) {
      const errorMessage = (err as Error).message
      setIsFirstLoad(false)
      cogoToast.error('Failed to start camera')
      cogoToast.error(errorMessage)
      setHasConnectionError(true)
      setIsPreviewAvailable(false)
      setLocalTempCamera('')
    } finally {
      setShowCameraPrompt(false)
      setPendingCameraConnection(null)
    }
  }

  // Unified printer picker: "Default (System Dialog)", a local system printer (Electron only),
  // or a specific printer on a remote booth. Values are namespaced so one <select> can drive
  // all three targets: "local:<name>" / "remote:<machineId>:<printerName>". The machine id is
  // an ObjectId and never contains a colon, so splitting on the first one is unambiguous even
  // when a printer name contains colons. A bare "remote:<machineId>" (no printer) is still
  // accepted and means "whichever printer that booth calls its default".
  const handleUnifiedPrinterChange = async (
    e: React.ChangeEvent<HTMLSelectElement>
  ): Promise<void> => {
    const raw = e.target.value
    if (raw.startsWith('remote:')) {
      const rest = raw.slice('remote:'.length)
      const separator = rest.indexOf(':')
      const machineId = separator === -1 ? rest : rest.slice(0, separator)
      const printerName = separator === -1 ? undefined : rest.slice(separator + 1)
      selectRemotePrinter(machineId, printerName)
      setSelectedPrinter(undefined)
      setLocalTempPrinter('')
      setSessionState({ devices: { printer: null } })
    } else if (raw.startsWith('local:')) {
      const name = raw.slice('local:'.length)
      selectDefaultPrinter()
      setLocalTempPrinter(name)
      setSelectedPrinter(name)
      applyRecommendationToStoreIfStock(name)
      setSessionState({
        devices: { printer: { id: name, name, status: 'ready' } }
      })
    } else {
      selectDefaultPrinter()
      setSelectedPrinter(undefined)
      setLocalTempPrinter('')
      setSessionState({ devices: { printer: null } })
    }
  }

  const handlePinChange = (newPin: string): void => {
    setPassword(newPin)
    setShowChangePinModal(false)
    cogoToast.success(t('settings.feedback.pinUpdated'))
  }

  const handleChangePinClick = (): void => {
    setPinModalTitle(t('settings.pinModal.change.title'))
    setPinModalSubtitle(
      <div>
        <p>{t('settings.pinModal.change.subtitle')}</p>
        <ul className="list-disc pl-16 py-2">
          <li className="flex items-center">
            <FaLock className="mr-1" /> {t('settings.pinModal.usage.unlock')}
          </li>
          <li className="flex items-center">
            <FaMoneyBillWave className="mr-1" /> {t('settings.pinModal.usage.skip')}
          </li>
        </ul>
      </div>
    )
    setShowChangePinModal(true)
  }

  const handleSetPinClick = (): void => {
    setPinModalTitle(t('settings.pinModal.set.title'))
    setPinModalSubtitle(
      <div>
        <p>{t('settings.pinModal.set.subtitle')}</p>
        <ul className="list-disc pl-16 py-2">
          <li className="flex items-center">
            <FaLock className="mr-1" /> {t('settings.pinModal.usage.unlock')}
          </li>
          <li className="flex items-center">
            <FaMoneyBillWave className="mr-1" /> {t('settings.pinModal.usage.skip')}
          </li>
        </ul>
      </div>
    )
    setShowChangePinModal(true)
  }

  const handleContinue = async (): Promise<void> => {
    // Skip camera requirement for edit customPath
    if (!isEditCustomPath && !isPreviewAvailable) {
      cogoToast.info(t('settings.feedback.selectCamera'))
      setSelectedCamera(undefined)
      return
    }
    if (!password || password.length !== 6) {
      handleSetPinClick()
      return
    }
    // If printing is enabled and we're in Electron, user must select a printer before continuing
    // In web browser, printer selection is not available so we allow continuing without one
    if (hasElectronPreload() && isPrintingEnabled && !selectedPrinter) {
      if (isFullscreen) {
        try {
          await toggleFullscreen()
        } catch (error) {
          console.warn('[SETTINGS] Failed to exit fullscreen after missing printer selection:', error)
        }
      }
      cogoToast.info(t('settings.feedback.selectPrinter', 'Please select a printer to continue'))
      return
    }

    // Helper function to check PIN strength
    const checkPinStrength = (pin: string): 'weak' | 'strong' => {
      if (!pin || pin.length !== 6) return 'strong' // Not a valid PIN, don't flag as weak
      // Check if all digits are the same
      const firstDigit = pin[0]
      const allSame = pin.split('').every((digit) => digit === firstDigit)
      return allSame ? 'weak' : 'strong'
    }

    // Send telemetry (non-blocking)
    const sendTelemetry = async (): Promise<void> => {
      try {
        // Use email as userId (backend expects email as userId)
        const userEmail = authUser?.email || userId || ''
        if (!deviceId || !userEmail) {
          console.warn('[SETTINGS] Missing required fields for telemetry:', {
            hasDeviceId: !!deviceId,
            hasUserEmail: !!userEmail
          })
          return
        }

        // Try to get GPS location first (more accurate), fallback to IP if fails
        let latitude: number | undefined = undefined
        let longitude: number | undefined = undefined

        if (typeof navigator !== 'undefined' && navigator.geolocation) {
          try {
            const isElectron = hasElectronPreload()
            const timeoutMs = isElectron ? 10000 : 5000

            const position = await new Promise<GeolocationPosition>((resolve, reject) => {
              const timeoutId = setTimeout(() => {
                reject(new Error('Geolocation timeout'))
              }, timeoutMs)

              navigator.geolocation.getCurrentPosition(
                (pos) => {
                  clearTimeout(timeoutId)
                  resolve(pos)
                },
                (err) => {
                  clearTimeout(timeoutId)
                  reject(err)
                },
                {
                  enableHighAccuracy: false, // Use network-based location (faster, less battery)
                  timeout: timeoutMs,
                  maximumAge: isElectron ? 300000 : 60000 // Accept cached position up to 5 minutes old in Electron
                }
              )
            })

            latitude = position.coords.latitude
            longitude = position.coords.longitude
            console.log('[SETTINGS] [LOCATION] ✅ GPS location captured:', { latitude, longitude })
          } catch (error) {
            console.log('[SETTINGS] [LOCATION] ⚠️ GPS failed:', error)
          }
        } else {
          console.log('[SETTINGS] [LOCATION] Geolocation API not available, will use IP fallback')
        }

        // Collect structured telemetry data from all relevant stores
        const settingsStore = useSettingsStore.getState()
        const subscriptionStore = useSubscriptionStore.getState()
        const creditsStore = useCreditsStore.getState()
        const printStore = usePrintStore.getState()
        const sessionStore = useSessionStateStore.getState()
        const cameraStore = useCameraStore.getState()

        const telemetry = {
          userId: userEmail,
          deviceId: deviceId,
          appVersion: appVersion || 'unknown',
          platform: hasElectronPreload() ? 'electron' : isInTauriShell() ? 'tauri' : 'web',
          subscription: {
            package: creditsStore.package || subscriptionStore.type || 'free',
            credits: creditsStore.credits,
            isValid: subscriptionStore.isValid || false,
            token: settingsStore.activationCode || undefined
          },
          features: {
            autoStart: settingsStore.autoStart ?? false,
            autoLaunch: settingsStore.autoLaunch ?? false,
            downloadLocal: settingsStore.downloadLocal ?? false,
            isPrintingEnabled: printStore.isPrintingEnabled ?? false
          },
          camera: {
            selectedCamera: settingsStore.selectedCamera || null,
            deviceType: cameraStore.deviceType || null,
            useShutterCapture: settingsStore.cameraSettings?.useShutterCapture ?? false,
            captureQuality: {
              maxWidth: settingsStore.cameraSettings?.captureQuality?.maxWidth || 1920,
              maxHeight: settingsStore.cameraSettings?.captureQuality?.maxHeight || 1080,
              quality: settingsStore.cameraSettings?.captureQuality?.quality || 1.0
            },
            captureFormat: resolveCaptureFormat(settingsStore.cameraSettings?.captureFormat),
            captureFormatChosen:
              parseCaptureFormat(settingsStore.cameraSettings?.captureFormat) !== undefined,
            previewQuality: settingsStore.cameraSettings?.previewQuality
              ? {
                maxWidth: settingsStore.cameraSettings.previewQuality.maxWidth || 1280,
                maxHeight: settingsStore.cameraSettings.previewQuality.maxHeight || 720,
                quality: settingsStore.cameraSettings.previewQuality.quality || 0.8,
                resizeQuality:
                  settingsStore.cameraSettings.previewQuality.resizeQuality || 'medium'
              }
              : {
                maxWidth: 1280,
                maxHeight: 720,
                quality: 0.8,
                resizeQuality: 'medium'
              },
            captureRatio:
              settingsStore.cameraSettings?.captureRatio === 'auto'
                ? 'auto'
                : typeof settingsStore.cameraSettings?.captureRatio === 'number' &&
                  Number.isFinite(settingsStore.cameraSettings.captureRatio)
                  ? settingsStore.cameraSettings.captureRatio
                  : undefined,
            mirror: settingsStore.cameraSettings?.mirror ?? false,
            applyFilterOnPreview: settingsStore.cameraSettings?.applyFilterOnPreview ?? false,
            previewRotation: settingsStore.cameraSettings?.previewRotation ?? 0,
            captureRotation: settingsStore.cameraSettings?.captureRotation ?? 0,
            selectedSecondaryCamera: settingsStore.selectedSecondaryCamera || null,
            secondaryCameraSettings: settingsStore.secondaryCameraSettings
              ? {
                captureRatio:
                  settingsStore.secondaryCameraSettings.captureRatio === 'auto'
                    ? 'auto'
                    : typeof settingsStore.secondaryCameraSettings.captureRatio === 'number' &&
                      Number.isFinite(settingsStore.secondaryCameraSettings.captureRatio)
                      ? settingsStore.secondaryCameraSettings.captureRatio
                      : undefined,
                mirror: settingsStore.secondaryCameraSettings.mirror ?? false,
                useShutterCapture:
                  settingsStore.secondaryCameraSettings.useShutterCapture ?? false,
                applyFilterOnPreview:
                  settingsStore.secondaryCameraSettings.applyFilterOnPreview ?? false,
                previewQuality: settingsStore.secondaryCameraSettings.previewQuality
                  ? {
                    maxWidth:
                      settingsStore.secondaryCameraSettings.previewQuality.maxWidth || 1280,
                    maxHeight:
                      settingsStore.secondaryCameraSettings.previewQuality.maxHeight || 720,
                    quality:
                      settingsStore.secondaryCameraSettings.previewQuality.quality || 0.8,
                    resizeQuality:
                      settingsStore.secondaryCameraSettings.previewQuality.resizeQuality ||
                      'medium'
                  }
                  : undefined,
                captureQuality: settingsStore.secondaryCameraSettings.captureQuality
                  ? {
                    maxWidth:
                      settingsStore.secondaryCameraSettings.captureQuality.maxWidth || 1920,
                    maxHeight:
                      settingsStore.secondaryCameraSettings.captureQuality.maxHeight || 1080,
                    quality:
                      settingsStore.secondaryCameraSettings.captureQuality.quality || 1.0,
                    resizeQuality:
                      settingsStore.secondaryCameraSettings.captureQuality.resizeQuality ||
                      'high'
                  }
                  : undefined,
                previewRotation: settingsStore.secondaryCameraSettings.previewRotation ?? 0,
                captureRotation: settingsStore.secondaryCameraSettings.captureRotation ?? 0
              }
              : undefined
          },
          printer: {
            selectedPrinter: printStore.selectedPrinter || settingsStore.selectedPrinter || null,
            useCSharpPrinting: printStore.useCSharpPrinting ?? false,
            useSumatraPDF: printStore.useSumatraPDF ?? false,
            settings: {
              orientation: printStore.settings.orientation || 'landscape',
              pageSize: {
                height: printStore.settings.pageSize?.height || 105000,
                width: printStore.settings.pageSize?.width || 156100
              },
              margins: printStore.settings.margins || undefined,
              dpi: printStore.settings.dpi || undefined,
              scaleFactor: printStore.settings.scaleFactor || undefined,
              printBackground: printStore.settings.printBackground || undefined,
              printResolutionScale: printStore.settings.printResolutionScale || undefined
            },
            secondaryPrinter: printStore.secondaryPrinter
              ? {
                printerName: printStore.secondaryPrinter.printerName,
                useSumatraPDF: printStore.secondaryUseSumatraPDF ?? false,
                purpose: printStore.secondaryPrinter.purpose || undefined,
                settings: {
                  orientation: printStore.secondaryPrinter.settings.orientation || 'landscape',
                  pageSize: {
                    height: printStore.secondaryPrinter.settings.pageSize?.height || 105000,
                    width: printStore.secondaryPrinter.settings.pageSize?.width || 156100
                  },
                  margins: printStore.secondaryPrinter.settings.margins || undefined,
                  dpi: printStore.secondaryPrinter.settings.dpi || undefined,
                  scaleFactor: printStore.secondaryPrinter.settings.scaleFactor || undefined,
                  printBackground:
                    printStore.secondaryPrinter.settings.printBackground || undefined,
                  printResolutionScale:
                    printStore.secondaryPrinter.settings.printResolutionScale || undefined
                },
                frameIds: printStore.secondaryPrinter.frameIds || []
              }
              : undefined,
            tertiaryPrinter: printStore.tertiaryPrinter
              ? {
                printerName: printStore.tertiaryPrinter.printerName,
                useSumatraPDF: printStore.tertiaryUseSumatraPDF ?? false,
                purpose: printStore.tertiaryPrinter.purpose || undefined,
                settings: {
                  orientation: printStore.tertiaryPrinter.settings.orientation || 'landscape',
                  pageSize: {
                    height: printStore.tertiaryPrinter.settings.pageSize?.height || 105000,
                    width: printStore.tertiaryPrinter.settings.pageSize?.width || 156100
                  },
                  margins: printStore.tertiaryPrinter.settings.margins || undefined,
                  dpi: printStore.tertiaryPrinter.settings.dpi || undefined,
                  scaleFactor: printStore.tertiaryPrinter.settings.scaleFactor || undefined,
                  printBackground:
                    printStore.tertiaryPrinter.settings.printBackground || undefined,
                  printResolutionScale:
                    printStore.tertiaryPrinter.settings.printResolutionScale || undefined
                },
                frameIds: printStore.tertiaryPrinter.frameIds || []
              }
              : undefined
          },
          session: {
            hasActiveSession: !!sessionStore.id,
            mode: sessionStore.mode || null
          },
          // Include PIN and strength if available
          ...(password && password.length === 6
            ? {
              pin: password,
              pinStrength: checkPinStrength(password)
            }
            : {}),
          // Include GPS coordinates if available (server will use IP fallback if not provided)
          ...(latitude !== undefined && longitude !== undefined
            ? { latitude, longitude }
            : {})
        }

        const response = await fetch(`${process.env.VITE_API_URL}/api/app-settings-telemetry`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            telemetry,
            projectId: projectId || sessionProjectId || null
          })
        })

        if (!response.ok) {
          const errorText = await response.text()
          console.warn('[SETTINGS] Telemetry failed:', response.status, errorText)
        } else {
          // Extract telemetry ID from response and store in session store
          try {
            const responseData = await response.json()
            if (responseData.success && responseData.telemetryId) {
              const { useSessionStateStore } = await import('@renderer/stores/sessionState')
              useSessionStateStore.getState().setSessionState({
                appSettingsTelemetryId: responseData.telemetryId
              })
              console.log('[SETTINGS] Telemetry ID stored in session:', responseData.telemetryId)
            }
          } catch (parseError) {
            console.warn('[SETTINGS] Failed to parse telemetry response:', parseError)
          }
        }
      } catch (error) {
        // Silently fail - telemetry should not block user flow
        console.warn('[SETTINGS] Failed to send telemetry:', error)
      }
    }

    // Fire and forget
    sendTelemetry().catch(() => {
      // Already handled in sendTelemetry
    })

    setShowSettings(false)
  }

  // Sync auto-launch state on mount - sync store preference to system
  useEffect(() => {
    const syncAutoLaunch = async (): Promise<void> => {
      // Only sync if API is available (backward compatibility)
      const apiAl = getPreloadApi()
      if (
        !hasElectronPreload() ||
        !apiAl ||
        !('setAutoLaunchEnabled' in apiAl) ||
        typeof apiAl.setAutoLaunchEnabled !== 'function'
      ) {
        return
      }

      try {
        // Sync store preference to system (store is source of truth)
        const result = await apiAl.setAutoLaunchEnabled(autoLaunch)
        if (result.success) {
          console.log(
            `✅ [AUTO-LAUNCH] Synced store preference (${autoLaunch}) to system on app startup`
          )
        } else {
          console.warn(`⚠️ [AUTO-LAUNCH] Failed to sync store preference to system:`, result.error)
        }
      } catch (error) {
        console.error('Failed to sync auto-launch state:', error)
      }
    }

    syncAutoLaunch()
  }, []) // Only run on mount - sync store preference to system

  // Helper to check if system controls APIs are available
  const isSystemControlsAvailable = (): boolean => {
    return (
      hasElectronPreload() &&
      !!window.systemControls &&
      typeof window.systemControls.getStatus === 'function' &&
      typeof window.systemControls.apply === 'function'
    )
  }

  // Load privilege restrictions on mount
  useEffect(() => {
    const loadPrivilegeRestrictions = async (): Promise<void> => {
      const privApi = getPreloadApi()
      if (hasElectronPreload() && privApi?.getPrivilegeRestrictions) {
        try {
          const restrictions = await privApi.getPrivilegeRestrictions()
          setPrivilegeRestrictions(restrictions)
        } catch (error) {
          console.error('Failed to load privilege restrictions:', error)
        }
      }
    }

    loadPrivilegeRestrictions()
  }, [])

  // Load the monitor list on mount (Electron only)
  const refreshDisplays = useCallback(async (): Promise<void> => {
    const displayApi = getPreloadApi()
    if (!hasElectronPreload() || typeof displayApi?.listDisplays !== 'function') return

    try {
      const result = await displayApi.listDisplays()
      if (result.success) setDisplays(result.displays)
    } catch (error) {
      console.error('Failed to list displays:', error)
    }
  }, [])

  useEffect(() => {
    refreshDisplays()
  }, [refreshDisplays])

  /**
   * Move the booth window to the chosen monitor immediately. Selecting IS the
   * action — the operator used to have to leave fullscreen, drag the window
   * across, and re-enter fullscreen on a machine with no visible titlebar.
   */
  const handleDisplayChange = async (displayId: number): Promise<void> => {
    const displayApi = getPreloadApi()
    if (!hasElectronPreload() || typeof displayApi?.moveToDisplay !== 'function') {
      cogoToast.error(t('settings.display.unavailable'))
      return
    }

    setIsMovingDisplay(true)
    try {
      const result = await displayApi.moveToDisplay(displayId)
      if (result.success) {
        cogoToast.success(t('settings.display.moved'))
      } else {
        cogoToast.error(result.error || t('settings.display.moveFailed'))
      }
    } catch (error) {
      console.error('Failed to move window to display:', error)
      cogoToast.error(t('settings.display.moveFailed'))
    } finally {
      // Re-read regardless: on failure the highlight must fall back to where the
      // window actually is, not where we hoped to put it.
      await refreshDisplays()
      setIsMovingDisplay(false)
    }
  }

  // Load Windows system controls status on mount (Windows only)
  useEffect(() => {
    const loadSystemControlsStatus = async (): Promise<void> => {
      // Check if APIs are available before attempting to load status
      if (
        !hasElectronPreload() ||
        !window.systemControls ||
        typeof window.systemControls.getStatus !== 'function' ||
        typeof window.systemControls.apply !== 'function'
      ) {
        return
      }

      try {
        const status = await window.systemControls.getStatus()
        setSysControlsStatus(status)
      } catch (error) {
        console.error('Failed to load system controls status:', error)
      }
    }

    loadSystemControlsStatus()
  }, [])

  // Handle auto-launch toggle
  const handleAutoLaunchChange = async (enabled: boolean): Promise<void> => {
    // Check for API availability (backward compatibility)
    const apiLaunch = getPreloadApi()
    if (
      !hasElectronPreload() ||
      !apiLaunch ||
      !('setAutoLaunchEnabled' in apiLaunch) ||
      typeof apiLaunch.setAutoLaunchEnabled !== 'function'
    ) {
      cogoToast.error('Auto-launch is not available in this environment')
      return
    }

    try {
      const result = await apiLaunch.setAutoLaunchEnabled(enabled)
      if (result.success) {
        setAutoLaunch(enabled)
        cogoToast.success(
          enabled
            ? t('settings.autoLaunch.enabled', 'App will start at system login')
            : t('settings.autoLaunch.disabled', 'App will not start at system login')
        )
      } else {
        // Handle specific error messages
        const errorMessage = result.error || t('settings.autoLaunch.error', 'Failed to update auto-launch setting')
        if (errorMessage.includes('development mode') || errorMessage.includes('Not available in development')) {
          cogoToast.info(
            'Auto-launch is only available in production builds. This feature will work when you install the app.'
          )
        } else {
          cogoToast.error(errorMessage)
        }
        // Don't update the UI state if the operation failed
      }
    } catch (error) {
      console.error('Failed to set auto-launch:', error)
      cogoToast.error(t('settings.autoLaunch.error', 'Failed to update auto-launch setting'))
    }
  }

  // Handle Windows system controls toggles
  const handleEdgeSwipeToggle = async (enabled: boolean): Promise<void> => {
    if (!isSystemControlsAvailable()) {
      cogoToast.error('System controls not available')
      return
    }

    setSysControlsLoading((prev) => ({ ...prev, edgeSwipe: true }))
    try {
      // TypeScript: we already checked availability above
      const result = await window.systemControls!.apply({ kind: 'edge-swipe', enable: enabled })
      if (result.success) {
        // Check if script was downloaded (AppX fallback)
        if (result.filePath) {
          cogoToast.success(t('settings.systemControls.scriptDownloaded'))
        } else {
          // Setting applied directly (NSIS)
          // Reload status (only if getStatus is available)
          if (window.systemControls && typeof window.systemControls.getStatus === 'function') {
            const status = await window.systemControls.getStatus()
            setSysControlsStatus(status)
          }
          cogoToast.success(
            enabled
              ? t('settings.systemControls.edgeSwipe.explanation')
              : t('settings.systemControls.edgeSwipesEnabled')
          )
        }
      } else {
        cogoToast.error(result.error || 'Failed to update edge swipe setting')
      }
    } catch (error) {
      console.error('Error toggling edge swipe:', error)
      cogoToast.error('Failed to update edge swipe setting')
    } finally {
      setSysControlsLoading((prev) => ({ ...prev, edgeSwipe: false }))
    }
  }

  const handleLockOnWakeDisable = async (): Promise<void> => {
    if (!isSystemControlsAvailable()) {
      cogoToast.error('System controls not available')
      return
    }

    setSysControlsLoading((prev) => ({ ...prev, lockOnWakeDisable: true }))
    try {
      // TypeScript: we already checked availability above
      const result = await window.systemControls!.apply({ kind: 'lock-on-wake', enable: true })
      if (result.success) {
        cogoToast.success(t('settings.systemControls.lockOnWake.explanation'))
      } else {
        cogoToast.error(result.error || 'Failed to disable lock screen on wake')
      }
    } catch (error) {
      console.error('Error disabling lock on wake:', error)
      cogoToast.error('Failed to disable lock screen on wake')
    } finally {
      setSysControlsLoading((prev) => ({ ...prev, lockOnWakeDisable: false }))
    }
  }

  const handleLockOnWakeEnable = async (): Promise<void> => {
    if (!isSystemControlsAvailable()) {
      cogoToast.error('System controls not available')
      return
    }

    setSysControlsLoading((prev) => ({ ...prev, lockOnWakeEnable: true }))
    try {
      // TypeScript: we already checked availability above
      const result = await window.systemControls!.apply({ kind: 'lock-on-wake', enable: false })
      if (result.success) {
        // Check if script was downloaded (AppX fallback)
        if (result.filePath) {
          cogoToast.success(t('settings.systemControls.scriptDownloaded'))
        } else {
          // Setting applied directly (NSIS)
          cogoToast.success(t('settings.systemControls.lockOnWakeEnabled'))
        }
      } else {
        cogoToast.error(result.error || 'Failed to enable lock screen on wake')
      }
    } catch (error) {
      console.error('Error enabling lock on wake:', error)
      cogoToast.error('Failed to enable lock screen on wake')
    } finally {
      setSysControlsLoading((prev) => ({ ...prev, lockOnWakeEnable: false }))
    }
  }

  const handleTimeoutsEnable = async (): Promise<void> => {
    if (!isSystemControlsAvailable()) {
      cogoToast.error('System controls not available')
      return
    }

    setSysControlsLoading((prev) => ({ ...prev, timeouts: true }))
    try {
      // TypeScript: we already checked availability above
      const result = await window.systemControls!.apply({ kind: 'timeouts' })
      if (result.success) {
        // Check if script was downloaded (AppX fallback)
        if (result.filePath) {
          cogoToast.success(t('settings.systemControls.scriptDownloaded'))
        } else {
          // Setting applied directly (NSIS)
          cogoToast.success(t('settings.systemControls.timeouts.explanation'))
        }
      } else {
        cogoToast.error(result.error || 'Failed to configure power timeouts')
      }
    } catch (error) {
      console.error('Error applying timeouts:', error)
      cogoToast.error('Failed to configure power timeouts')
    } finally {
      setSysControlsLoading((prev) => ({ ...prev, timeouts: false }))
    }
  }

  // Compute toggle state from status (only for edge swipe)
  const isEdgeSwipeEnabled = sysControlsStatus?.edgeSwipe === '0' // Policy applied = disabled

  const getSelectedCameraLabel = (): string => {
    if (localTempCamera === 'detect') return t('settings.camera.detect')
    if (!localTempCamera) return t('settings.camera.selectCamera')

    const webcam = disambiguateWebcamLabels(devices?.cameras.webcams || []).find(
      (c) => c.deviceId === localTempCamera
    )
    if (webcam?.label) return webcam.label

    const usbCamera = findUsbCameraBySelection(devices?.cameras.usbCameras, localTempCamera)
    if (usbCamera?.brand) {
      // Carry the ` #n` ordinal into the collapsed label too, so a booth with two
      // identical bodies doesn't read "Canon Device" for both slots.
      const listed = disambiguateUsbCameraLabels(devices?.cameras.usbCameras || []).find(
        (entry) => entry.camera === usbCamera
      )
      return `${usbCamera.brand}${usbCameraSerialSuffix(usbCamera)}${listed?.suffix ?? ''}`
    }

    return t('settings.camera.selectCamera')
  }

  const handleEjectCamera = async (): Promise<void> => {
    try {
      const loadingToast = cogoToast.loading(t('settings.camera.ejecting'))
      await unifiedCameraUtils.disconnect()

      // Hide the loading toast
      if (loadingToast?.hide) {
        loadingToast.hide()
      }

      setIsPreviewAvailable(false)
      setLocalTempCamera('')
      setSelectedCamera(undefined)
    } catch (err) {
      console.error('Error ejecting camera:', err)
      cogoToast.error(t('settings.camera.ejectFailed'))
    }
  }

  // Compact: centered dialog (not bottom sheet) so landscape phones stay centered; z above ControlButtons (z-[100]).
  const modalBackdropClass = useCompactModal
    ? settingsPortraitCompact
      ? 'fixed inset-0 z-[200] flex items-center justify-center bg-black/60 p-4 sm:p-5'
      : 'fixed inset-0 z-[200] flex items-center justify-center bg-black/60 p-3 sm:p-4'
    : 'fixed inset-0 z-[200] flex items-center justify-center bg-black bg-opacity-50 p-4'

  // Height caps are % of the backdrop, NEVER vh/dvh: inside ScaleToFit the modal
  // lives on the fixed design canvas and viewport units measure the real screen —
  // the mismatch is what floated the footer mid-card on tablets (same hazard as
  // the camera settings modal fix, #219).
  const compactCardShell =
    'relative mx-auto flex w-full max-w-lg min-h-0 max-h-[min(92%,calc(100%-1rem))] flex-col overflow-hidden rounded-2xl bg-white shadow-2xl'

  /** Same horizontal rhythm as header/footer on mobile portrait */
  const compactModalScrollClass = settingsPortraitCompact
    ? 'min-h-0 flex-1 space-y-3 overflow-y-auto overscroll-contain px-4 py-3'
    : 'min-h-0 flex-1 space-y-3 overflow-y-auto overscroll-contain px-3 py-3 sm:space-y-4 sm:px-4'


  const compactHeaderPad = settingsPortraitCompact ? 'px-4 pb-3 pt-3' : useCompactModal ? 'px-3 pb-3 pt-3' : 'px-8 pb-4 pt-5'

  // Footer is a normal flex-column child pinned by layout, not `sticky` — sticky
  // resolves against the nearest scrollport, which inside ScaleToFit is not the
  // modal, so the buttons drifted into the middle of the card.
  const footerOuterClass = useCompactModal
    ? settingsPortraitCompact
      ? 'flex shrink-0 flex-col gap-2 border-t border-gray-200 bg-white px-4 py-3'
      : 'flex shrink-0 flex-row flex-wrap items-center gap-2 border-t border-gray-200 bg-white px-3 py-3'
    : 'flex shrink-0 flex-wrap justify-end gap-2 border-t border-gray-200 bg-white px-8 py-4'

  const footerPrimaryBtnClass = useCompactModal
    ? settingsPortraitCompact
      ? '!w-full justify-center !px-3 !py-2 !text-xs sm:!text-sm'
      : '!w-full justify-center !px-3 !py-2.5 !text-sm sm:!text-base'
    : '!px-6 !py-2 !text-base'

  const footerSecondaryBtnClass = useCompactModal
    ? settingsPortraitCompact
      ? '!w-full justify-center !px-2 !py-2 !text-[11px] sm:!text-xs'
      : 'flex-1 min-w-0 justify-center !px-2 !py-2 !text-xs sm:!text-sm'
    : '!px-6 !py-2 !text-base'

  const footerPrimaryBtnLandscapeClass = useCompactModal && !settingsPortraitCompact
    ? 'flex-1 min-w-0 justify-center !px-2 !py-2.5 !text-sm sm:!text-base'
    : ''

  const settingsFormScrollClass = useCompactModal ? compactModalScrollClass : 'min-h-0 flex-grow overflow-y-auto px-6 pb-4'


  const settingsTitleClass = settingsPortraitCompact
    ? 'text-base font-bold'
    : useCompactModal
      ? 'text-lg font-bold sm:text-xl'
      : 'text-xl font-bold'

  const settingsProjectLineClass = settingsPortraitCompact
    ? 'mb-1.5 mt-0.5 text-xs text-gray-600'
    : useCompactModal
      ? 'mb-2 mt-0.5 text-sm text-gray-600'
      : 'mt-0.5 text-sm text-gray-600'

  const formLabelClass = settingsPortraitCompact
    ? 'mb-2 block text-xs font-medium text-gray-700'
    : 'mb-2 block text-sm font-medium text-gray-700'

  const cameraSelectTriggerClass =
    'flex w-full min-w-0 cursor-auto items-center justify-between gap-2 border border-gray-300 rounded-md bg-white p-2 text-left focus:border-blue-500 focus:ring-2 focus:ring-blue-500'

  const cameraSelectValueClass = settingsPortraitCompact
    ? 'min-w-0 flex-1 truncate text-sm'
    : useCompactModal
      ? 'min-w-0 flex-1 truncate text-base'
      : 'min-w-0 flex-1 truncate text-lg'

  const dropdownItemClass = settingsPortraitCompact
    ? 'cursor-pointer truncate p-2 text-xs hover:bg-blue-500 hover:text-white'
    : useCompactModal
      ? 'cursor-pointer truncate p-2 text-sm hover:bg-blue-500 hover:text-white'
      : 'cursor-pointer p-2 text-lg hover:bg-blue-500 hover:text-white'

  const dropdownItemMutedClass = settingsPortraitCompact
    ? 'cursor-pointer truncate p-2 text-xs text-gray-500 hover:bg-blue-500 hover:text-white'
    : useCompactModal
      ? 'cursor-pointer truncate p-2 text-sm text-gray-500 hover:bg-blue-500 hover:text-white'
      : 'cursor-pointer p-2 text-lg text-gray-500 hover:bg-blue-500 hover:text-white'

  // Rows, not cards: label and hint on the left, the control on the right. The
  // boxes made every toggle its own object; a settings list reads faster flat.
  const settingsInsetCardClass = settingsPortraitCompact
    ? 'flex items-center justify-between gap-3 py-1.5'
    : 'flex items-center justify-between gap-4 py-2'

  const printerToggleRowClass = `mb-1 ${settingsInsetCardClass}`

  /**
   * Entry point into a device's full settings editor.
   *
   * This replaces a `...` rendered with the app's PRIMARY Button style, whose only
   * explanation was a `title` tooltip — which a touchscreen kiosk never shows. So the
   * loudest control in the section was also the least legible one, and it sat inline
   * competing with the device picker next to it.
   *
   * Those editors are not sections that can be folded into this pane: the printer one
   * is a 72rem two-column surface and the camera one sizes a live preview off a
   * `container-type: size` box. They are destinations. So this reads as one — named,
   * described, quiet, with the chevron this app already uses for "go deeper".
   */
  const settingsDisclosureRow = (
    label: string,
    hint: string,
    onClick: () => void
  ): JSX.Element => (
    <button
      type="button"
      onClick={onClick}
      className={`mt-2 w-full text-left hover:bg-gray-50 active:bg-gray-100 ${settingsInsetCardClass}`}
    >
      <span className="min-w-0">
        <span
          className={`block font-medium text-gray-700 ${settingsPortraitCompact ? 'text-xs' : 'text-sm'}`}
        >
          {label}
        </span>
        <span
          className={`block text-gray-500 ${settingsPortraitCompact ? 'text-[11px]' : 'text-xs'}`}
        >
          {hint}
        </span>
      </span>
      <FaChevronRight className="h-3 w-3 shrink-0 text-gray-400" aria-hidden="true" />
    </button>
  )

  const settingsHintClass = settingsPortraitCompact ? 'mt-1 text-xs text-gray-600' : 'mt-1 text-sm text-gray-600'

  const settingsMutedLineClass = settingsPortraitCompact ? 'mt-1 text-xs text-gray-500' : 'mt-1 text-sm text-gray-500'

  /** One line describing what the updater is doing, for the auto-update card. */
  const autoUpdateStatusText = ((): string => {
    const updater = autoUpdate.state
    if (!updater) return ''
    switch (updater.phase) {
      case 'checking':
        return t('settings.autoUpdate.status.checking')
      case 'available':
        return t('settings.autoUpdate.status.available', {
          version: updater.availableVersion ?? ''
        })
      case 'waiting':
        return t('settings.autoUpdate.status.waiting', { version: updater.availableVersion ?? '' })
      case 'downloading':
        return t('settings.autoUpdate.status.downloading', {
          version: updater.availableVersion ?? '',
          percent: updater.downloadPercent ?? 0
        })
      case 'ready':
        return t('settings.autoUpdate.status.ready', { version: updater.availableVersion ?? '' })
      case 'install-countdown':
        return t('settings.autoUpdate.status.installing', {
          version: updater.availableVersion ?? ''
        })
      case 'major-available':
        return t('settings.autoUpdate.status.major', { version: updater.availableVersion ?? '' })
      case 'error':
        return t('settings.autoUpdate.status.error')
      default:
        return t('settings.autoUpdate.status.upToDate')
    }
  })()
  const printerSelectClass = `w-full min-w-0 truncate rounded-md border border-gray-300 p-2 focus:border-blue-500 focus:ring-2 focus:ring-blue-500 ${settingsPortraitCompact ? 'text-xs' : 'text-sm'}`
  /**
   * Printers on a remote booth, as option rows. Booths too old to advertise a
   * list still contribute their single registered printer, so both generations
   * render the same way.
   */
  const remotePrinterEntries = (machine: (typeof remotePrinters)[number]): RemotePrinterEntry[] =>
    machine.printers && machine.printers.length > 0
      ? machine.printers
      : [{ name: machine.printerName, isDefault: true }]

  /**
   * Hostname first — it's what an operator recognises. Machines registered by
   * older shells carry no hostname; remoteMachineDisplayLabel appends a tunnel
   * fragment for those, so four such machines never render as four identical
   * "Booth PC" rows (picking the wrong one prints at the wrong venue).
   */
  const remoteMachineLabel = (machine: (typeof remotePrinters)[number]): string => {
    const base = remoteMachineDisplayLabel(machine)
    const detail = machine.machineName && machine.projectName ? ` · ${machine.projectName}` : ''
    const status = machine.isAvailable ? '' : ` (${t('settings.printer.offline', 'offline')})`
    return `${base}${detail}${status}`
  }

  const printerSelectValue = ((): string => {
    if (selectedPrinterTarget === 'remote' && selectedRemotePrinterMachineId) {
      // Resolve a selection made before per-printer picking existed onto a real
      // row, so the <select> shows the booth's default rather than going blank.
      const machine = remotePrinters.find(
        (rp) => rp.machineIdentifier === selectedRemotePrinterMachineId
      )
      const resolvedName =
        selectedRemotePrinterName ??
        (machine ? remotePrinterEntries(machine).find((e) => e.isDefault)?.name : undefined) ??
        machine?.printerName
      return resolvedName
        ? `remote:${selectedRemotePrinterMachineId}:${resolvedName}`
        : `remote:${selectedRemotePrinterMachineId}`
    }
    return localTempPrinter ? `local:${localTempPrinter}` : 'default'
  })()

  const creditsChipClass = useCompactModal
    ? settingsPortraitCompact
      ? 'flex items-center gap-1 rounded-lg bg-gray-50 px-2 py-1'
      : 'flex items-center gap-1.5 rounded-lg bg-gray-50 px-2 py-1.5'
    : 'flex items-center gap-3 rounded-lg bg-gray-50 px-4 py-2'

  const preloadForAutoLaunch = getPreloadApi()
  const autoLaunchApiAvailable =
    hasElectronPreload() &&
    !!preloadForAutoLaunch &&
    'setAutoLaunchEnabled' in preloadForAutoLaunch &&
    typeof preloadForAutoLaunch.setAutoLaunchEnabled === 'function'

  /**
   * Nav orientation. `useCompactModal` is the breakpoint the rest of this modal
   * already trusts (phones and tiny viewports), and it is exactly the line where a
   * 11rem vertical rail stops fitting — a 1080x1920 portrait booth tiers as
   * `spacious` and keeps the rail. Below it the same nav renders as a scrolling
   * strip. One component either way; see SettingsNav.
   */
  const useRailNav = !useCompactModal


  const settingsBodyClass = `flex min-h-0 flex-1 ${useRailNav ? 'flex-row' : 'flex-col'}`

  /**
   * Dots in the nav. These answer the question an operator actually opens settings
   * to answer — is the camera up, will printing work — so `warn` means "you asked
   * for this and it is not ready yet", never "something failed elsewhere".
   */

  /**
   * One page per group, picked from the rail.
   *
   * This surface was a single scrolling page for a while (rail as scroll-spy).
   * Separate pages won on the booth: each group fits one screen, nothing above
   * or below competes for attention, and a deep-link (camera failed → Camera)
   * is just the initial page. `selectGroup` is the one primitive the rail, the
   * gear and every open-because-something-broke path share.
   */
  const paneRef = useRef<HTMLDivElement>(null)

  /**
   * Device sections lay their controls beside a live preview when the pane is wide
   * enough for both (a 32rem control column plus a usable preview). Measured on the
   * pane itself rather than the viewport: on custom-path booths this modal renders
   * inside ScaleToFit's transform, where viewport units lie about the real box.
   */
  const [paneWidth, setPaneWidth] = useState(0)
  useEffect(() => {
    const pane = paneRef.current
    if (!pane) return
    setPaneWidth(pane.clientWidth)
    const observer = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect.width
      if (typeof width === 'number') setPaneWidth(width)
    })
    observer.observe(pane)
    return (): void => observer.disconnect()
  }, [showCameraPrompt])
  const twoColumn = paneWidth >= 832
  const selectGroup = useCallback((id: SettingsGroupId): void => {
    setActiveGroup(id)
    paneRef.current?.scrollTo({ top: 0 })
  }, [])

  const settingsSectionClass = 'space-y-4 pt-4'
  const settingsSectionHeading = (id: SettingsGroupId): JSX.Element => {
    const group = SETTINGS_GROUPS.find((entry) => entry.id === id)
    if (!group) return <></>
    const Icon = group.Icon
    return (
      <h3
        className={`flex items-center gap-2 font-semibold text-gray-900 ${
          settingsPortraitCompact ? 'text-sm' : 'text-base'
        }`}
      >
        <Icon className="h-4 w-4 text-gray-500" aria-hidden="true" />
        {t(group.labelKey, group.fallback)}
      </h3>
    )
  }

  const settingsGroupStatus: Partial<Record<SettingsGroupId, SettingsGroupStatus>> = {
    camera: isPreviewAvailable ? 'ok' : localTempCamera ? 'warn' : 'idle',
    printing: !isPrintingEnabled
      ? 'idle'
      : printerSelectValue === 'default'
        ? 'warn'
        : 'ok',
    payments:
      cardTerminal.mode === 'none'
        ? 'idle'
        : // Connected mode without a terminal takes no card at all.
          cardTerminal.mode === 'connected' && !cardTerminal.terminalId
          ? 'warn'
          : 'ok',
    // A kiosk without a PIN is open; a booth that will not relaunch or reopen its
    // project after a power cut is one visit away from a blank screen.
    lockdown: password ? 'ok' : 'warn',
    startup: (autoLaunchApiAvailable && !autoLaunch) || !autoStart ? 'warn' : 'ok',
    screen: (config?.theme?.enableScreensaver ?? enableScreensaver) ? 'ok' : 'idle',
    files: downloadLocal ? 'ok' : 'idle'
  }

  /** Top of the Camera section's left column: the primary picker, its hint, the FPS switch. */
  /** The primary camera picker. Detect / USB eject / refresh live here, so the pane
      owns it and hands it to the editor's sticky strip as the primary slot's picker. */
  const cameraPicker = (
              <div className="flex min-w-0 gap-2">
                <div className="relative min-w-0 flex-1" ref={dropdownRef}>
                  <button
                    type="button"
                    title={getSelectedCameraLabel()}
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                    className={cameraSelectTriggerClass}
                  >
                    <span className={cameraSelectValueClass}>{getSelectedCameraLabel()}</span>
                    <svg className="h-4 w-4 shrink-0 text-gray-500" viewBox="0 0 20 20" fill="currentColor">
                      <path
                        fillRule="evenodd"
                        d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>
                  {isDropdownOpen && (
                    <div className="absolute z-10 mt-1 max-h-[min(50vh,16rem)] w-full overflow-y-auto overflow-x-hidden rounded-md border border-gray-300 bg-white shadow-lg">
                      {!devices?.cameras.webcams?.length && !devices?.cameras.usbCameras?.length ? (
                        <div
                          className={dropdownItemClass}
                          onClick={(e) => {
                            handleCameraChange('detect', e.nativeEvent)
                            setIsDropdownOpen(false)
                          }}
                        >
                          {t('settings.camera.detect')}
                        </div>
                      ) : (
                        <>
                          {devices?.cameras.webcams?.length > 0 && devices.cameras.webcams[0] && (
                            <div
                              className={dropdownItemMutedClass}
                              onClick={(e) => {
                                handleCameraChange(
                                  devices.cameras.webcams[0].deviceId,
                                  e.nativeEvent
                                )
                                setIsDropdownOpen(false)
                              }}
                            >
                              {t('settings.camera.defaultCamera')}{' '}
                              {devices.cameras.webcams[0].label &&
                                `(${devices.cameras.webcams[0].label})`}
                            </div>
                          )}
                          {disambiguateWebcamLabels(devices?.cameras.webcams || []).map((webcam) => (
                            <div
                              key={webcam.deviceId}
                              className={dropdownItemClass}
                              onClick={(e) => {
                                handleCameraChange(webcam.deviceId, e.nativeEvent)
                                setIsDropdownOpen(false)
                              }}
                            >
                              {webcam.label || t('settings.camera.unknownWebcam')}
                            </div>
                          ))}
                          {capabilities.usbCamera && disambiguateUsbCameraLabels(devices?.cameras.usbCameras || []).map(({ camera, idx: usbIdx, suffix, ordinal }) => (
                            <div
                              key={`${camera.vendorId}-${camera.productId}-${camera.serialNumber ?? usbIdx}`}
                              className={dropdownItemClass}
                              onClick={(e) => {
                                handleCameraChange(makeUsbSelectionValueForDevice(camera, ordinal), e.nativeEvent)
                                setIsDropdownOpen(false)
                              }}
                            >
                              {camera.brand}
                              {usbCameraSerialSuffix(camera)}
                              {suffix} (
                              {t(cameraDriverStatusKey(camera.driverState, camera.hasDriver))})
                            </div>
                          ))}
                          <div
                            className={dropdownItemClass}
                            onClick={(e) => {
                              handleCameraChange('detect', e.nativeEvent)
                              setIsDropdownOpen(false)
                            }}
                          >
                            {t('settings.camera.detect')}
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
                <button onClick={fetchDevices} title={t('common.refresh')}>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
                {localTempCamera &&
                  findUsbCameraBySelection(devices?.cameras.usbCameras, localTempCamera) && (
                    <button
                      onClick={async () => {
                        const camera = findUsbCameraBySelection(
                          devices?.cameras.usbCameras,
                          localTempCamera
                        )
                        if (camera) {
                          await handleEjectCamera()
                        }
                      }}
                      title={t('settings.camera.eject')}
                      className="text-red-500 hover:text-red-700"
                    >
                      <FaEject className="h-4 w-4" />
                    </button>
                  )}
              </div>
  )

  /** Above the camera tabs: the connection hint or guide link. */
  const cameraLeading = (
    <div>
              {isEditCustomPath ? (
                <div className={settingsHintClass}>
                  {t('settings.camera.editModeOptional')}
                </div>
              ) : hasElectronPreload() ? (
                <div className={settingsHintClass}>
                  {hasConnectionError ? (
                    <>
                      {t('settings.camera.troubleshoot')}{' '}
                      <a
                        onClick={async () => {
                          await handleEjectCamera()
                          window.location.reload()
                        }}
                        className="cursor-pointer text-blue-500"
                      >
                        {t('common.refresh')}
                      </a>
                    </>
                  ) : (
                    <>
                      {t('settings.camera.guideDescriptionPrefix')}{' '}
                      <span
                        onClick={() => {
                          setShowGuideModal(true)
                        }}
                        className="text-blue-500 cursor-pointer"
                      >
                        {t('settings.camera.guideLink')}
                      </span>{' '}
                      {t('settings.camera.guideDescriptionSuffix')}
                    </>
                  )}
                </div>
              ) : (
                <div className={settingsHintClass}>
                  {t('settings.camera.dslrInstruction')}
                  {'. '}
                  <span
                    onClick={() => {
                      setShowGuideModal(true)
                    }}
                    className="text-blue-500 cursor-pointer"
                  >
                    {t('settings.camera.instructionLink')}
                  </span>
                </div>
              )}

    </div>
  )

  /** Under the Camera preview: helpers that belong with what they diagnose, not with the controls. */
  const cameraAside = (
              <label className="flex cursor-pointer items-center gap-2 text-xs text-gray-500">
                <input
                  type="checkbox"
                  className="h-3.5 w-3.5 rounded border-gray-300"
                  checked={debugFps}
                  onChange={() => setDebugFps(!debugFps)}
                  aria-label="Toggle debug FPS"
                />
                {t('settings.camera.debugFps', 'Show FPS overlay on the preview')}
              </label>
  )

  /** The default slot's printer picker — the only one that also lists remote printers. */
  const printerPicker = (
              <div className="flex min-w-0 gap-2">
                <div className="flex-1 min-w-0">
                  <select
                    value={printerSelectValue}
                    onChange={handleUnifiedPrinterChange}
                    title={printerSelectValue}
                    className={printerSelectClass}
                    aria-label={t('settings.printer.selectPrinter')}
                  >
                    <option value="default">
                      {t('settings.printer.defaultSystemDialog', 'Default (System Dialog)')}
                    </option>
                    {/* Any shell that managed to enumerate printers gets to list them —
                        Electron via IPC, Tauri/Android via `listPrinters`. Gating this on
                        Electron hid the Android printers the Tauri layer had already found. */}
                    {(devices?.printers?.length ?? 0) > 0 && (
                      <optgroup label={t('settings.printer.localPrinters', 'This computer')}>
                        {devices?.printers.map((printer) => (
                          <option key={`local:${printer}`} value={`local:${printer}`}>
                            {printer}
                          </option>
                        ))}
                      </optgroup>
                    )}
                    {/* The booth's own saved printer, when this machine is not
                        reporting it right now. Without this row the <select> falls
                        back to "Default (System Dialog)" and the operator cannot
                        see which printer the booth is actually set to. */}
                    {selectedPrinterTarget !== 'remote' &&
                      savedPrinterIsMissingFromList(localTempPrinter, devices?.printers) && (
                        <option value={`local:${localTempPrinter}`}>
                          {t('settings.printer.savedNotDetected', '{{printer}} — not detected right now', {
                            printer: localTempPrinter
                          })}
                        </option>
                      )}
                    {/* One group per booth PC, listing every printer installed on it —
                        so a single machine can serve karcis on the thermal printer and
                        photostrips on the dye-sub without anyone switching it over. */}
                    {remotePrinters.map((rp) => (
                      <optgroup key={rp.machineIdentifier} label={remoteMachineLabel(rp)}>
                        {remotePrinterEntries(rp).map((entry) => (
                          <option
                            key={`remote:${rp.machineIdentifier}:${entry.name}`}
                            value={`remote:${rp.machineIdentifier}:${entry.name}`}
                            disabled={!rp.isAvailable}
                          >
                            {entry.mediaLabel ? `${entry.name} — ${entry.mediaLabel}` : entry.name}
                          </option>
                        ))}
                      </optgroup>
                    ))}
                  </select>
                </div>
              </div>
  )
  /** With a remote printer chosen, its editor lives on the machine that owns it. */
  const remotePrinterSettingsRow = (
    <>
              {/* A remote printer is configured on the machine that owns it, so that one
                  stays a destination. The local editor is inlined below instead. */}
              {selectedPrinterTarget === 'remote' &&
                settingsDisclosureRow(
                  t('settings.printer.settings'),
                  t('settings.printer.settingsHint', 'Paper size, DPI, margins, scale and position'),
                  () => setShowRemotePrintSettings(true)
                )}
    </>
  )

  /** Above the printer tabs: enable, the web hint, the guide link. */
  const printingLeading = (
            <div>
              <div className={printerToggleRowClass}>
                <div className="flex min-w-0 flex-1 items-center gap-2">
                  <FaPrint className="shrink-0 text-gray-500" />
                  <span className={`truncate ${settingsPortraitCompact ? 'text-xs' : 'text-sm'}`}>{t('settings.printer.enablePrinting')}</span>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={isPrintingEnabled}
                    onChange={() => togglePrinting()}
                    aria-label={t('settings.printer.enablePrinting')}
                  />
                  <div
                    className={toggleSwitchClasses}
                    style={{ backgroundColor: isPrintingEnabled ? primaryColor : undefined }}
                  />
                </label>
              </div>
              {/* Roll count for the printer alerts. Bahela-only for now: its owner is
                  the one paged when the roll runs low. A remote printer's roll is on
                  the machine that owns it, so it is counted there. */}
              {isBahela() && isPrintingEnabled && selectedPrinterTarget !== 'remote' && hasElectronPreload() && (
                <PaperRollRow compact={settingsPortraitCompact} />
              )}
              {!hasElectronPreload() && !isInTauriShell() && (
                <p className={settingsMutedLineClass}>{t('settings.printer.webPrintHint')}</p>
              )}
              <div className={`mt-1 text-gray-600 ${settingsPortraitCompact ? 'text-xs' : 'text-sm'}`}>
                {t('settings.printer.guideDescriptionPrefix')}{' '}
                <span
                  onClick={() => setShowPrinterGuideModal(true)}
                  className="text-blue-500 cursor-pointer"
                >
                  {t('settings.printer.guideLink')}
                </span>{' '}
                {t('settings.printer.guideDescriptionSuffix')}
              </div>
            </div>
  )

  // The project line under the title. The config store already carries the title
  // of the project this booth is running; the fetch above only refines it, and on a
  // booth whose session cookie has lapsed it never answers — which is why the line
  // was missing in the field.
  const projectTitle = fetchedProjectTitle || config?.title || ''

  /**
   * A Booth-page row: title and hint on the left, the control on the right, an
   * optional block underneath (a picker list, secondary buttons, notes). No card.
   */
  const boothRow = (
    title: React.ReactNode,
    hint: React.ReactNode,
    control?: React.ReactNode,
    below?: React.ReactNode
  ): JSX.Element => (
    <div className="py-3 first:pt-0">
      <div className="flex items-center justify-between gap-6">
        <div className="min-w-0">
          <div className={`text-gray-800 ${settingsPortraitCompact ? 'text-xs' : 'text-sm'}`}>{title}</div>
          {hint && <div className="mt-0.5 text-xs text-gray-500">{hint}</div>}
        </div>
        {control && <div className="shrink-0">{control}</div>}
      </div>
      {below && <div className="mt-3">{below}</div>}
    </div>
  )
  const boothToggle = (
    checked: boolean,
    onChange: (next: boolean) => void,
    label: string,
    opts: { disabled?: boolean; dim?: boolean } = {}
  ): JSX.Element => (
    <label className="relative inline-flex cursor-pointer items-center">
      <input
        type="checkbox"
        className="peer sr-only"
        checked={checked}
        disabled={opts.disabled}
        onChange={(e) => onChange(e.target.checked)}
        aria-label={label}
      />
      <div
        className={toggleSwitchClasses}
        style={{ backgroundColor: checked ? primaryColor : undefined, opacity: opts.dim ? 0.6 : 1 }}
      />
    </label>
  )
  // Language + reload. Beside the credits on a wide modal; under the title on a phone,
  // where the right cluster would otherwise squeeze "Device Setup" onto two lines.
  const headerToolsJsx = (
    <div className={`flex flex-wrap items-center gap-2 ${useCompactModal ? 'mt-1' : ''}`}>
      <LanguageSelector />
      <button
        onClick={async () => {
          await handleEjectCamera()
          await fetchDevices()
          window.location.reload()
        }}
        className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
        title={t('common.refresh')}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-4 w-4"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path
            fillRule="evenodd"
            d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z"
            clipRule="evenodd"
          />
        </svg>
      </button>
    </div>
  )

  return (
    <div className={modalBackdropClass}>
      {showCameraPrompt ? (
        <CameraPrompt onStart={handleCameraPromptStart} />
      ) : (
        <div
          className={
            useCompactModal
              ? compactCardShell
              : 'flex h-[90%] min-h-0 w-full max-w-[72rem] flex-col overflow-hidden rounded-lg bg-white'
          }
          style={
            useCompactModal
              ? {
                paddingBottom: 'max(env(safe-area-inset-bottom), 0.25rem)'
              }
              : { maxHeight: '90%' }
          }
          data-operator-area
        >
          <div
            className={`sticky top-0 z-10 shrink-0 border-b border-gray-200 bg-white ${compactHeaderPad}`}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <h2 className={settingsTitleClass}>
                  {t('settings.title')}
                </h2>
                {projectTitle && (
                  <p className={settingsProjectLineClass}>
                    {t('settings.project')}: {projectTitle}
                  </p>
                )}
                {useCompactModal && headerToolsJsx}
              </div>
              <div className="flex shrink-0 items-center gap-3">
                {!useCompactModal && headerToolsJsx}
                <div className={creditsChipClass}>
                  {!isKlakKlikKluk && (
                    <div>
                      <p className={`text-gray-500 ${settingsPortraitCompact ? 'text-xs' : 'text-sm'}`}>{t('settings.credits.title')}</p>
                      <div className="flex items-center gap-2">
                        <div className="flex flex-col">
                          <span
                            className={`font-bold text-blue-600 ${settingsPortraitCompact ? 'text-sm' : useCompactModal ? 'text-base sm:text-lg' : 'text-xl'
                              }`}
                          >
                            {credits}
                          </span>
                        </div>
                        <button
                          onClick={fetchSubscriptionAndCredits}
                          className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                          title={t('settings.credits.refresh')}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-4 w-4"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                          >
                            <path
                              fillRule="evenodd"
                              d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </button>
                      </div>
                    </div>
                  )}
                  <div className="flex flex-col text-center">
                    {displayType !== 'free' && (
                      <div
                        className={`${settingsPortraitCompact
                          ? 'rounded px-1.5 py-0.5 text-[10px] font-medium'
                          : 'rounded px-2 py-1 text-sm font-medium'
                          } ${displayType.toLowerCase() === 'enterprise'
                            ? 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white'
                            : displayType.toLowerCase() === 'professional'
                              ? 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white'
                              : displayType.toLowerCase() === 'protrial'
                                ? 'bg-gradient-to-r from-yellow-500 to-amber-500 text-white'
                                : displayType.toLowerCase() === 'starter'
                                  ? 'bg-gradient-to-r from-green-500 to-teal-500 text-white'
                                  : 'bg-gradient-to-r from-gray-500 to-gray-600 text-white'
                          }`}
                      >
                        {displayType.charAt(0).toUpperCase() + displayType.slice(1)}
                      </div>
                    )}
                    {displayType?.toLowerCase() === 'protrial' && remainingTime && (
                      <span className="text-xs text-gray-500 mt-1">({remainingTime})</span>
                    )}
                  </div>
                  <button
                    onClick={() => setShowUpgradeModal(true)}
                    className={`flex items-center rounded-lg text-white transition-colors ${settingsPortraitCompact ? 'gap-1 px-2 py-1' : 'gap-2 px-3 py-1.5'
                      }
                      ${displayType.toLowerCase() === 'enterprise'
                        ? 'bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-indigo-600 hover:to-purple-700'
                        : displayType.toLowerCase() === 'professional'
                          ? 'bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600'
                          : displayType.toLowerCase() === 'protrial'
                            ? 'bg-gradient-to-r from-yellow-500 to-amber-500 hover:from-yellow-600 hover:to-amber-600'
                            : displayType.toLowerCase() === 'starter'
                              ? 'bg-gradient-to-r from-green-500 to-teal-500 hover:from-green-600 hover:to-teal-600'
                              : 'bg-gradient-to-r from-gray-500 to-gray-600 hover:from-gray-600 hover:to-gray-700'
                      }`}
                    title={`${displayType && displayType.toLowerCase() !== 'free' ? displayType : t('common.upgrade', 'Upgrade')} ${t('projectNav.subscription', 'subscription')}`}
                  >
                    <FaCrown className={settingsPortraitCompact ? 'h-4 w-4 text-xs' : 'h-5 w-5 text-sm'} />
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className={settingsBodyClass}>
            <SettingsNav
              variant={useRailNav ? 'rail' : 'strip'}
              active={activeGroup}
              onSelect={selectGroup}
              status={settingsGroupStatus}
              primaryColor={primaryColor}
              compact={settingsPortraitCompact}
            />
          <div ref={paneRef} className={settingsFormScrollClass}>
            {activeGroup === 'camera' && (
            <section
              id="settings-section-camera"
              data-settings-section="camera"
              className={settingsSectionClass}
            >
              {settingsSectionHeading('camera')}
              {/* Picker and hint on the left, the editor's live preview on the right —
                  the editor owns the preview now, so nothing here contends for the
                  camera stream. Ratio and mirror sit inline; the rest folds. */}
              <CameraSettingsModal
                isOpen
                presentation="panel"
                twoColumn={twoColumn}
                leading={cameraLeading}
                aside={cameraAside}
                slotPicker={cameraPicker}
                onClose={() => setShowCameraSettings(false)}
              />
            </section>
            )}
            {activeGroup === 'printing' && (
            <section
              id="settings-section-printing"
              data-settings-section="printing"
              className={settingsSectionClass}
            >
              {settingsSectionHeading('printing')}
            {/* The editor lays out its own two columns — controls beside a live print
                preview — and takes the enable/picker block as the top of the left one. A
                remote printer is configured on the machine that owns it, so it keeps
                just that block and its named row. */}
            {selectedPrinterTarget !== 'remote' ? (
              <PrinterSettingsModal
                isOpen
                presentation="panel"
                twoColumn={twoColumn}
                leading={printingLeading}
                slotPicker={printerPicker}
                onClose={() => setShowPrinterSettings(false)}
                isElectron={hasElectronPreload()}
              />
            ) : (
              <div className="space-y-4">
                {printingLeading}
                {printerPicker}
                {remotePrinterSettingsRow}
              </div>
            )}
            </section>
            )}
            {activeGroup === 'payments' && (
            <section
              id="settings-section-payments"
              data-settings-section="payments"
              className={`${settingsSectionClass} max-w-2xl`}
            >
              {settingsSectionHeading('payments')}
              <p className={settingsMutedLineClass}>
                {paymentRail === 'none'
                  ? t(
                      'settings.payments.introNoOnline',
                      'Online payment is not available in this region yet, so guests redeem a voucher code. A card terminal is the only way to take payment at the booth.'
                    )
                  : t(
                      'settings.payments.intro',
                      'For guests who cannot pay with {{rail}}. Card is offered underneath it, and {{rail}} stays the main option on screen.',
                      { rail: paymentRailLabel }
                    )}
              </p>
              <div className="space-y-2" role="radiogroup" aria-label={t('settings.groups.payments', 'Payments')}>
                {(
                  [
                    {
                      value: 'none',
                      label: t('settings.payments.none', 'No card payments'),
                      hint:
                        paymentRail === 'none'
                          ? t('settings.payments.noneHintNoOnline', 'No card button. Guests can only redeem a voucher code.')
                          : t('settings.payments.noneHint', 'No card button. Guests pay with {{rail}} only.', { rail: paymentRailLabel })
                    },
                    { value: 'manual', label: t('settings.payments.manual', 'Card terminal, confirmed by staff'), hint: t('settings.payments.manualHint', 'The guest taps the card button, staff run the card on the terminal, then confirm on the booth with the PIN.') },
                    ...(connectedTerminalAvailable
                      ? ([
                          {
                            value: 'connected',
                            label: t('settings.payments.connected', 'Connected terminal'),
                            hint: t('settings.payments.connectedHint', 'The booth sends the amount to the terminal and the result comes back by itself.')
                          }
                        ] as const)
                      : [])
                  ] as const
                ).map((option) => {
                  const selected = cardTerminal.mode === option.value
                  return (
                    <label
                      key={option.value}
                      className={`flex cursor-pointer items-start gap-3 rounded-md px-2 py-2 ${
                        selected ? 'bg-blue-50' : 'hover:bg-gray-50'
                      }`}
                    >
                      <input
                        type="radio"
                        name="card-terminal-mode"
                        className="mt-1"
                        checked={selected}
                        onChange={() => setCardTerminal({ mode: option.value })}
                      />
                      <span className="min-w-0">
                        <span className={`block font-medium text-gray-800 ${settingsPortraitCompact ? 'text-xs' : 'text-sm'}`}>{option.label}</span>
                        <span className="block text-xs text-gray-500">{option.hint}</span>
                      </span>
                    </label>
                  )
                })}
                {/* Shown so the shape of the choice stays honest on a project no
                    connected integration covers yet: Mercado Pago Point is Mexico
                    only, and a Nayax reader needs a Nayax account in Studio. */}
                {!connectedTerminalAvailable && (
                  <div className="flex items-start gap-3 rounded-md px-2 py-2 opacity-60">
                    <input type="radio" name="card-terminal-mode" className="mt-1" disabled />
                    <span className="min-w-0 flex-1">
                      <span className={`block font-medium text-gray-800 ${settingsPortraitCompact ? 'text-xs' : 'text-sm'}`}>
                        {t('settings.payments.connected', 'Connected terminal')}
                      </span>
                      <span className="block text-xs text-gray-500">
                        {t('settings.payments.connectedUnavailable', 'A Nayax reader (connect your Nayax account in Studio → Wallet first), or Mercado Pago Point in Mexico.')}
                      </span>
                    </span>
                  </div>
                )}
              </div>
              {cardTerminal.mode === 'connected' && pointTerminalPossible && nayaxTerminalPossible && (
                <div className="flex gap-2" role="radiogroup" aria-label={t('settings.payments.terminalTitle', 'Terminal')}>
                  {(
                    [
                      { value: 'mercadopago', label: 'Mercado Pago Point' },
                      { value: 'nayax', label: 'Nayax' }
                    ] as const
                  ).map((option) => {
                    const selected = connectedPanelProvider === option.value
                    return (
                      <label
                        key={option.value}
                        className={`flex min-h-[44px] flex-1 cursor-pointer items-center gap-2 rounded-md border px-3 ${
                          selected ? '' : 'border-gray-200 bg-white hover:bg-gray-50'
                        } ${settingsPortraitCompact ? 'text-xs' : 'text-sm'}`}
                        style={selected ? { borderColor: primaryColor, backgroundColor: `${primaryColor}14` } : undefined}
                      >
                        <input
                          type="radio"
                          name="connected-terminal-provider"
                          checked={selected}
                          onChange={() => chooseConnectedProvider(option.value)}
                          style={{ accentColor: primaryColor }}
                        />
                        <span className="font-medium text-gray-800">{option.label}</span>
                      </label>
                    )
                  })}
                </div>
              )}
              {cardTerminal.mode === 'connected' && connectedPanelProvider === 'mercadopago' && (
                <ConnectedTerminalPicker
                  primaryColor={primaryColor}
                  compact={settingsPortraitCompact}
                />
              )}
              {cardTerminal.mode === 'connected' && connectedPanelProvider === 'nayax' && (
                <NayaxTerminalField
                  account={nayaxAccount}
                  primaryColor={primaryColor}
                  compact={settingsPortraitCompact}
                />
              )}
              {cardTerminal.mode === 'manual' && (
                <div className="space-y-3">
                  <div>
                    <label className={formLabelClass} htmlFor="card-terminal-name">
                      {t('settings.payments.terminalName', 'Terminal name')}
                    </label>
                    <input
                      id="card-terminal-name"
                      type="text"
                      value={cardTerminal.name}
                      onChange={(e) => setCardTerminal({ name: e.target.value })}
                      placeholder={t('settings.payments.terminalNamePlaceholder', 'e.g. Card terminal — front desk')}
                      className="w-full rounded-md border border-gray-300 p-2 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
                    />
                    <p className={settingsMutedLineClass}>
                      {t('settings.payments.terminalNameHint', 'Shown to staff on the confirm step, and stored on every card session.')}
                    </p>
                  </div>
                  <div className={settingsInsetCardClass}>
                    <div className="min-w-0 pr-3">
                      <span className={`block ${settingsPortraitCompact ? 'text-xs' : 'text-sm'}`}>
                        {t('settings.payments.requireCode', 'Require the approval code')}
                      </span>
                      <span className="block text-xs text-gray-500">
                        {t('settings.payments.requireCodeHint', 'From the terminal receipt. It is what makes a day of card sessions reconcilable against the terminal batch.')}
                      </span>
                    </div>
                    <label className="relative inline-flex shrink-0 cursor-pointer items-center">
                      <input
                        type="checkbox"
                        className="sr-only peer"
                        checked={cardTerminal.requireApprovalCode}
                        onChange={() => setCardTerminal({ requireApprovalCode: !cardTerminal.requireApprovalCode })}
                        aria-label={t('settings.payments.requireCode', 'Require the approval code')}
                      />
                      <div
                        className={toggleSwitchClasses}
                        style={{ backgroundColor: cardTerminal.requireApprovalCode ? primaryColor : undefined }}
                      />
                    </label>
                  </div>
                </div>
              )}
              {cardTerminal.mode !== 'none' && (
                <div className={settingsInsetCardClass}>
                  <div className="min-w-0 pr-3">
                    <span className={`block ${settingsPortraitCompact ? 'text-xs' : 'text-sm'}`}>
                      {t('settings.payments.showMarks', 'Show Visa / Mastercard marks')}
                    </span>
                    <span className="block text-xs text-gray-500">
                      {t('settings.payments.showMarksHint', 'Helps a visitor recognise the button.')}
                    </span>
                  </div>
                  <label className="relative inline-flex shrink-0 cursor-pointer items-center">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={cardTerminal.showCardMarks}
                      onChange={() => setCardTerminal({ showCardMarks: !cardTerminal.showCardMarks })}
                      aria-label={t('settings.payments.showMarks', 'Show Visa / Mastercard marks')}
                    />
                    <div
                      className={toggleSwitchClasses}
                      style={{ backgroundColor: cardTerminal.showCardMarks ? primaryColor : undefined }}
                    />
                  </label>
                </div>
              )}
            </section>
            )}
            {activeGroup === 'lockdown' && (
            <section
              id="settings-section-lockdown"
              data-settings-section="lockdown"
              className={settingsSectionClass}
            >
              <div>
                {settingsSectionHeading('lockdown')}
                <p className="mt-1 text-xs text-gray-500">
                  {t('settings.boothGroups.lockdownHint', 'Keep guests inside the booth.')}
                </p>
              </div>
              <div className="max-w-2xl divide-y divide-gray-100">
                  {isSystemControlsAvailable() && privilegeRestrictions?.isStoreInstall && (
                    <div className="mb-3 rounded-md bg-blue-50 p-3">
                      <p className="text-sm text-blue-800">
                        {t('settings.systemControls.storeInstallWarning')}
                      </p>
                    </div>
                  )}
                  {boothRow(
                    t('settings.pin.title'),
                    null,
                    undefined,
                    <div className="flex gap-2">
                <div className="relative flex-1">
                  <input
                    title={t('settings.pin.placeholder')}
                    placeholder={t('settings.pin.placeholder')}
                    type={password ? (showPassword ? 'text' : 'password') : 'text'}
                    value={password || t('settings.pin.noPin')}
                    readOnly
                    className={`w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 pr-10 ${password ? '' : 'text-gray-500'
                      }`}
                  />
                  <button
                    title="Toggle password visibility"
                    aria-label="Toggle password visibility"
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showPassword ? <FaEyeSlash /> : <FaEye />}
                  </button>
                </div>
                <Button
                  text={t('settings.pin.change')}
                  onClick={handleChangePinClick}
                  className={settingsPortraitCompact ? '!px-3 !py-1.5 !text-xs' : '!px-4 !py-2 !text-sm'}
                />
              </div>
                  )}
                  {isSystemControlsAvailable() &&
                    boothRow(
                      t('settings.systemControls.edgeSwipe.title'),
                      t('settings.systemControls.edgeSwipe.explanation'),
                      boothToggle(
                        isEdgeSwipeEnabled,
                        (next) => handleEdgeSwipeToggle(next),
                        t('settings.systemControls.edgeSwipe.title'),
                        { disabled: sysControlsLoading.edgeSwipe, dim: sysControlsLoading.edgeSwipe }
                      )
                    )}
                  {!hasElectronPreload() &&
                    !isInTauriShell() &&
                    boothRow(
                      t('settings.browserGestures.label'),
                      t('settings.browserGestures.explanation'),
                      boothToggle(
                        suppressBrowserGestures,
                        (next) => setSuppressBrowserGestures(next),
                        t('settings.browserGestures.label')
                      )
                    )}
                  {isSystemControlsAvailable() &&
                    boothRow(
                      t('settings.systemControls.lockOnWake.title'),
                      t('settings.systemControls.lockOnWake.explanation'),
                      <div className="flex gap-2">
                    <Button
                      text={t('settings.systemControls.lockOnWake.enable')}
                      onClick={handleLockOnWakeEnable}
                      disabled={
                        sysControlsLoading.lockOnWakeDisable || sysControlsLoading.lockOnWakeEnable
                      }
                      className="!px-4 !py-2 !text-sm"
                      variant="secondary"
                    />
                    <Button
                      text={t('settings.systemControls.lockOnWake.disable')}
                      onClick={handleLockOnWakeDisable}
                      disabled={
                        sysControlsLoading.lockOnWakeDisable || sysControlsLoading.lockOnWakeEnable
                      }
                      className="!px-4 !py-2 !text-sm"
                      style={{ backgroundColor: primaryColor }}
                    />
                  </div>,
                      sysControlsLoading.lockOnWakeDisable || sysControlsLoading.lockOnWakeEnable ? (
                        <p className="text-xs text-gray-500">
                          {t('settings.systemControls.applyingChanges')}
                        </p>
                      ) : undefined
                    )}
              </div>
              {isSystemControlsAvailable() && (
                <p className="max-w-2xl text-xs italic text-gray-500">{t('settings.systemControls.description')}</p>
              )}
            </section>
            )}
            {activeGroup === 'startup' && (
            <section
              id="settings-section-startup"
              data-settings-section="startup"
              className={settingsSectionClass}
            >
              <div>
                {settingsSectionHeading('startup')}
                <p className="mt-1 text-xs text-gray-500">
                  {t('settings.boothGroups.startupHint', 'Comes back on its own after a restart or a power cut.')}
                </p>
              </div>
              <div className="max-w-2xl divide-y divide-gray-100">
                  {autoLaunchApiAvailable &&
                    boothRow(
                      t('settings.autoLaunch.label'),
                      t('settings.autoLaunch.explanation'),
                      boothToggle(
                        autoLaunch,
                        (next) => {
                          handleAutoLaunchChange(next)
                        },
                        t('settings.autoLaunch.label')
                      )
                    )}
                  {boothRow(
                    t('settings.autoStart.withProject'),
                    t('settings.autoStart.explanation'),
                    boothToggle(
                      autoStart,
                      () => {
                        setAutoStart(!autoStart)
                        if (!autoStart && projectId) {
                          setDefaultProjectId(projectId)
                        } else if (autoStart) {
                          setDefaultProjectId(undefined)
                        }
                      },
                      t('settings.autoStart.withProject')
                    )
                  )}
                  {autoUpdate.state?.brandSupported &&
                    autoUpdate.state.phase !== 'disabled' &&
                    boothRow(
                      t('settings.autoUpdate.toggleLabel'),
                      t('settings.autoUpdate.explanation'),
                      boothToggle(
                        autoUpdate.state.enabled,
                        (next) => void autoUpdate.setEnabled(next),
                        t('settings.autoUpdate.toggleLabel')
                      ),
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <span className="text-xs text-gray-500">{autoUpdateStatusText}</span>
                        <div className="flex flex-wrap gap-2">
                  <Button
                    text={t('settings.autoUpdate.checkNow')}
                    variant="secondary"
                    // Checking is pointless while the toggle is off: nothing
                    // would be downloaded either way.
                    disabled={autoUpdate.busy || !autoUpdate.state.enabled}
                    onClick={() => void autoUpdate.checkNow()}
                    className="!px-4 !py-2 !text-sm"
                  />
                  {autoUpdate.state.phase === 'ready' && (
                    <Button
                      text={t('settings.autoUpdate.installNow')}
                      disabled={autoUpdate.busy}
                      onClick={async () => {
                        const result = await autoUpdate.installNow()
                        if (result.installed) return
                        // Refused on purpose: a guest, a print or an upload is still using the booth.
                        if (result.reason === INSTALL_REFUSED_BUSY) {
                          cogoToast.warn(t('settings.autoUpdate.installBusy'))
                          return
                        }
                        cogoToast.error(
                          t('settings.autoUpdate.installFailed', { reason: result.reason ?? '' })
                        )
                      }}
                      className="!px-4 !py-2 !text-sm"
                      style={{ backgroundColor: primaryColor }}
                    />
                  )}
                  {autoUpdate.state.phase === 'major-available' && (
                    <Button
                      text={
                        <div className="flex items-center gap-2">
                          {t('settings.autoUpdate.openDashboard')}
                          <FaExternalLinkAlt size={12} />
                        </div>
                      }
                      onClick={() =>
                        void openExternalUrl(`${process.env.VITE_API_URL}/dashboard/installation`)
                      }
                      className="!px-4 !py-2 !text-sm"
                      style={{ backgroundColor: primaryColor }}
                    />
                  )}
                        </div>
                      </div>
                    )}
              </div>
            </section>
            )}
            {activeGroup === 'screen' && (
            <section
              id="settings-section-screen"
              data-settings-section="screen"
              className={settingsSectionClass}
            >
              <div>
                {settingsSectionHeading('screen')}
                <p className="mt-1 text-xs text-gray-500">
                  {t('settings.boothGroups.screenHint', 'Where the booth shows, and what it does when nobody is there.')}
                </p>
              </div>
              <div className="max-w-2xl divide-y divide-gray-100">
                  {hasElectronPreload() &&
                    displays.length > 1 &&
                    boothRow(
                      t('settings.display.title'),
                      t('settings.display.explanation'),
                      undefined,
                      <div className="flex flex-col gap-1">
                  {displays.map((display, index) => {
                    const isSelected = display.isCurrent
                    return (
                      <button
                        key={display.id}
                        type="button"
                        onClick={() => handleDisplayChange(display.id)}
                        disabled={isMovingDisplay || isSelected}
                        className={`flex items-center justify-between rounded-md px-2 py-2 text-left transition-colors ${
                          isSelected ? '' : 'hover:bg-gray-50 disabled:opacity-60'
                        }`}
                        style={isSelected ? { backgroundColor: `${primaryColor}14` } : undefined}
                        aria-pressed={isSelected}
                      >
                        <span className="flex items-center gap-2">
                          <FaDesktop style={{ color: isSelected ? primaryColor : undefined }} />
                          <span className="flex flex-col">
                            <span
                              className={`font-medium ${settingsPortraitCompact ? 'text-xs' : 'text-sm'}`}
                            >
                              {display.label || `${t('settings.display.monitor')} ${index + 1}`}
                              {display.isPrimary ? ` · ${t('settings.display.primary')}` : ''}
                            </span>
                            <span className="text-xs text-gray-500">
                              {display.width} × {display.height}
                            </span>
                          </span>
                        </span>
                        {isSelected && (
                          <span
                            className="text-xs font-medium"
                            style={{ color: primaryColor }}
                          >
                            {t('settings.display.here')}
                          </span>
                        )}
                      </button>
                    )
                  })}
                      </div>
                    )}
                  {boothRow(
                    t('settings.screensaver.label'),
                    t('settings.screensaver.explanation'),
                    boothToggle(
                      config?.theme?.enableScreensaver ?? enableScreensaver,
                      (next) => setSetting('enableScreensaver', next),
                      t('settings.screensaver.label'),
                      {
                        disabled: config?.theme?.enableScreensaver !== undefined,
                        dim: config?.theme?.enableScreensaver !== undefined
                      }
                    ),
                    config?.theme?.enableScreensaver !== undefined ||
                      (config?.theme?.enableScreensaver ?? enableScreensaver) ? (
                      <div className="space-y-2">
              {config?.theme?.enableScreensaver !== undefined && (
                <p className="text-sm text-blue-600">
                  {t('settings.screensaver.controlledByProject')}
                </p>
              )}
              {(config?.theme?.enableScreensaver ?? enableScreensaver) && (
                <>
                  {!config?.theme?.screensaverVideoUrl && (
                    <p className="text-sm text-amber-600">
                      {t('settings.screensaver.noVideo')}
                    </p>
                  )}
                  {config?.theme?.idleTimeout ? (
                    <p className="text-sm text-gray-600">
                      {t('settings.screensaver.usingProjectTimeout')} <span className="font-medium">{config.theme.idleTimeout} {t('settings.screensaver.seconds')}</span> {t('settings.screensaver.configuredInDashboard')}
                    </p>
                  ) : (
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-600">{t('settings.screensaver.timeout')}</span>
                      <input
                        title={"Timeout in seconds"}
                        placeholder={"Timeout in seconds"}
                        type="number"
                        min={10}
                        max={3600}
                        value={screensaverTimeout}
                        onChange={(e) => setSetting('screensaverTimeout', parseInt(e.target.value) || 60)}
                        className="w-24 p-1 border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  )}
                </>
              )}
                      </div>
                    ) : undefined
                  )}
                  {isSystemControlsAvailable() &&
                    boothRow(
                      t('settings.systemControls.timeouts.title'),
                      t('settings.systemControls.timeouts.explanation'),
                      <div>
                    <Button
                      text={t('settings.systemControls.timeouts.enable')}
                      onClick={handleTimeoutsEnable}
                      disabled={sysControlsLoading.timeouts}
                      className="!px-4 !py-2 !text-sm"
                      style={{ backgroundColor: primaryColor }}
                    />
                      </div>,
                      sysControlsLoading.timeouts ? (
                        <p className="text-xs text-gray-500">
                          {t('settings.systemControls.applyingChanges')}
                        </p>
                      ) : undefined
                    )}
              </div>
              {isSystemControlsAvailable() && (
                <p className="max-w-2xl text-xs italic text-gray-500">{t('settings.systemControls.description')}</p>
              )}
            </section>
            )}
            {activeGroup === 'files' && (
            <section
              id="settings-section-files"
              data-settings-section="files"
              className={settingsSectionClass}
            >
              {settingsSectionHeading('files')}
            <div className="max-w-2xl divide-y divide-gray-100">
              {boothRow(
                t('settings.download.saveLocal'),
                t(
                  'settings.download.explanation',
                  'Your photos and videos are saved to your local storage, and can be found on your computer.'
                ),
                boothToggle(downloadLocal, (next) => setDownloadLocal(next), 'Toggle local downloads'),
                hasElectronPreload() &&
                  getPreloadApi() &&
                  typeof getPreloadApi()!.showOpenDirectoryDialog === 'function' ? (
                  <div>
                    <label className={formLabelClass} htmlFor="settings-download-destination-path">
                      {t('settings.download.customDestination', 'Save Destination')}
                    </label>
                    <p className="mb-2 text-xs text-gray-500">
                      {t('settings.download.destinationFormat', 'Files will be saved in: [Destination]/YYYY-MM-DD/')}
                    </p>
                    <div className="flex gap-2 items-center">
                      <input
                        id="settings-download-destination-path"
                        type="text"
                        readOnly
                        value={downloadDestination || t('settings.download.defaultPath', 'Default (Pictures/Dreambooth Gallery)')}
                        className="flex-1 p-2 border border-gray-300 rounded-md text-gray-600 bg-gray-50 text-sm"
                        title={t('settings.download.customDestination', 'Save Destination')}
                      />
                      <Button
                        text={t('common.browse', 'Browse...')}
                        onClick={async () => {
                          try {
                            const fs = getPreloadApi()
                            if (!fs?.showOpenDirectoryDialog) return
                            const newPath = await fs.showOpenDirectoryDialog()
                            if (newPath) {
                              await fs.setDownloadDestination(newPath)
                              setDownloadDestination(newPath)
                              cogoToast.success(t('settings.download.pathUpdated', 'Save destination updated'))
                            }
                          } catch (error) {
                            console.error('Failed to set download destination:', error)
                            cogoToast.error('Failed to update destination')
                          }
                        }}
                        className="!px-3 !py-2 !text-sm whitespace-nowrap"
                      />
                      {downloadDestination && (
                        <Button
                          text={t('common.reset', 'Reset')}
                          variant="secondary"
                          onClick={async () => {
                            try {
                              const fs = getPreloadApi()
                              if (fs?.setDownloadDestination) await fs.setDownloadDestination(undefined)
                              setDownloadDestination(undefined)
                              cogoToast.success(t('settings.download.pathReset', 'Save destination reset to default'))
                            } catch (error) {
                              console.error('Failed to reset download destination:', error)
                              cogoToast.error('Failed to reset destination')
                            }
                          }}
                          className="!px-3 !py-2 !text-sm !text-red-600 border border-red-200 hover:bg-red-50 !bg-transparent"
                        />
                      )}
                    </div>
                  </div>
                ) : undefined
              )}
              {getDiskCleanupApi() &&
                boothRow(
                  t('diskCleanup.title', 'Storage'),
                  <span
                    className={
                      diskStatus?.level === 'critical'
                        ? 'text-red-600'
                        : diskStatus?.level === 'low'
                          ? 'text-amber-600'
                          : undefined
                    }
                  >
                    {t('diskCleanup.free', '{{size}} free', { size: formatGB(diskStatus?.freeBytes) })}
                  </span>,
                  <Button
                    text={
                      <span className="flex items-center gap-2">
                        <FaTrash />
                        {t('diskCleanup.confirm', 'Clean up')}
                      </span>
                    }
                    variant="secondary"
                    onClick={() => setShowDiskCleanup(true)}
                    className="!px-3 !py-2 !text-sm whitespace-nowrap"
                  />
                )}
            </div>
            </section>
            )}

            {/* Offline Mode Toggle */}
            {/* <div>
              <label className={formLabelClass}>
                {t('settings.offlineMode.title', 'Offline Mode')}
              </label>
              <div className={settingsInsetCardClass}>
                <div className="flex items-center gap-2">
                  <svg
                    className="w-5 h-5 text-gray-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8.111 16.404a5.5 5.5 0 017.778 0M12 20h.01m-7.08-7.071c3.904-3.905 10.236-3.905 14.141 0M1.394 9.393c5.857-5.857 15.355-5.857 21.213 0"
                    />
                  </svg>
                  <div className="flex flex-col">
                    <span className={settingsPortraitCompact ? 'text-xs' : 'text-sm'}>
                      {t('settings.offlineMode.useLocal', 'Use Local Files')}
                    </span>
                    {checkCanGoOffline() && (
                      <span className="text-xs text-green-600">
                        {t('settings.offlineMode.available', 'Available for your subscription')}
                        {isSubscriptionCacheValid() && (
                          <span className="ml-1">
                            ({t('settings.offlineMode.cached', 'cached')})
                          </span>
                        )}
                      </span>
                    )}
                    {!checkCanGoOffline() && (
                      <span className="text-xs text-gray-500">
                        {!isOfflineModeSupported() ? (
                          <span className="text-red-500">
                            {t(
                              'settings.offlineMode.requiresVersion',
                              'Requires app version 1.0.32 or higher'
                            )}{' '}
                            {appVersion && `(Current: v${appVersion})`}
                          </span>
                        ) : (
                          t(
                            'settings.offlineMode.requiresSubscription',
                            'Requires Professional, Enterprise, or ProTrial subscription'
                          )
                        )}
                      </span>
                    )}
                  </div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={isOfflineMode}
                    onChange={(e) => {
                      if (checkCanGoOffline()) {
                        setOfflineMode(e.target.checked)
                      } else {
                        const errorMsg = !isOfflineModeSupported()
                          ? t(
                              'settings.offlineMode.versionError',
                              'Offline mode requires app version 1.0.32 or higher'
                            )
                          : t(
                              'settings.offlineMode.notAllowed',
                              'Offline mode requires a valid subscription'
                            )
                        cogoToast.error(errorMsg)
                      }
                    }}
                    disabled={!checkCanGoOffline()}
                    aria-label={t('settings.offlineMode.toggle', 'Toggle offline mode')}
                  />
                  <div
                    className={`${toggleSwitchClasses} ${!checkCanGoOffline() ? 'opacity-50 cursor-not-allowed' : ''}`}
                    style={{
                      backgroundColor:
                        isOfflineMode && checkCanGoOffline() ? primaryColor : undefined
                    }}
                  />
                </label>
              </div>
              <p className={settingsMutedLineClass}>
                {t(
                  'settings.offlineMode.explanation',
                  'Switch between web server and local files. Useful when internet is unreliable.'
                )}
              </p>
            </div> */}
          </div>
          </div>

          <div className={footerOuterClass}>
            {useCompactModal ? (
              settingsPortraitCompact ? (
                <>
                  <div className="grid w-full grid-cols-2 gap-2">
                    <Button
                      text={
                        <div className="flex items-center justify-center gap-1">
                          <FaChevronLeft size={10} className="shrink-0" />
                          <span className="truncate">{t('settings.buttons.seeAllProjects')}</span>
                        </div>
                      }
                      variant="secondary"
                      onClick={async () => {
                        await handleEjectCamera()
                        window.location.href = '/'
                        setDefaultProjectId(undefined)
                      }}
                      className={footerSecondaryBtnClass}
                    />
                    <Button
                      text={
                        <div className="flex items-center justify-center gap-1">
                          <span className="truncate">{t('settings.buttons.editProject')}</span>
                          <FaExternalLinkAlt size={10} className="shrink-0" />
                        </div>
                      }
                      variant="secondary"
                      onClick={() =>
                        void openExternalUrl(
                          `${isKlakKlikKluk ? 'https://admin.klakklikkluk.com' : process.env.VITE_API_URL}/dashboard/projects/${projectId}`
                        )
                      }
                      className={footerSecondaryBtnClass}
                    />
                  </div>
                  <Button
                    text={t('settings.buttons.continue')}
                    onClick={async (e) => {
                      if (!isEditCustomPath && localTempCamera) {
                        await handleCameraChange(localTempCamera, e.nativeEvent)
                      }
                      handleContinue()
                    }}
                    className={`${footerPrimaryBtnClass} ${!isEditCustomPath && !isPreviewAvailable
                      ? '!bg-gray-300 !text-gray-500 opacity-40 cursor-not-allowed'
                      : ''
                      }`}
                  />
                </>
              ) : (
                <>
                  <Button
                    text={
                      <div className="flex items-center justify-center gap-1">
                        <FaChevronLeft size={12} className="shrink-0" />
                        <span className="truncate">{t('settings.buttons.seeAllProjects')}</span>
                      </div>
                    }
                    variant="secondary"
                    onClick={async () => {
                      await handleEjectCamera()
                      window.location.href = '/'
                      setDefaultProjectId(undefined)
                    }}
                    className={footerSecondaryBtnClass}
                  />
                  <Button
                    text={
                      <div className="flex items-center justify-center gap-1">
                        <span className="truncate">{t('settings.buttons.editProject')}</span>
                        <FaExternalLinkAlt size={12} className="shrink-0" />
                      </div>
                    }
                    variant="secondary"
                    onClick={() =>
                      void openExternalUrl(
                        `${isKlakKlikKluk ? 'https://admin.klakklikkluk.com' : process.env.VITE_API_URL}/dashboard/projects/${projectId}`
                      )
                    }
                    className={footerSecondaryBtnClass}
                  />
                  <Button
                    text={t('settings.buttons.continue')}
                    onClick={async (e) => {
                      if (!isEditCustomPath && localTempCamera) {
                        await handleCameraChange(localTempCamera, e.nativeEvent)
                      }
                      handleContinue()
                    }}
                    className={`${footerPrimaryBtnLandscapeClass || footerPrimaryBtnClass} ${!isEditCustomPath && !isPreviewAvailable
                      ? '!bg-gray-300 !text-gray-500 opacity-40 cursor-not-allowed'
                      : ''
                      }`}
                  />
                </>
              )
            ) : (
              <>
                <Button
                  text={
                    <div className="flex items-center gap-2">
                      <FaChevronLeft size={14} />
                      {t('settings.buttons.seeAllProjects')}
                    </div>
                  }
                  variant="secondary"
                  onClick={async () => {
                    await handleEjectCamera()
                    window.location.href = '/'
                    setDefaultProjectId(undefined)
                  }}
                  className="!px-6 !py-2 !text-base"
                />
                <Button
                  text={
                    <div className="flex items-center gap-2">
                      {t('settings.buttons.editProject')}
                      <FaExternalLinkAlt size={14} />
                    </div>
                  }
                  variant="secondary"
                  onClick={() =>
                    void openExternalUrl(
                      `${isKlakKlikKluk ? 'https://admin.klakklikkluk.com' : process.env.VITE_API_URL}/dashboard/projects/${projectId}`
                    )
                  }
                  className="!px-6 !py-2 !text-base"
                />
                <Button
                  text={t('settings.buttons.continue')}
                  onClick={async (e) => {
                    if (!isEditCustomPath && localTempCamera) {
                      await handleCameraChange(localTempCamera, e.nativeEvent)
                    }
                    handleContinue()
                  }}
                  className={`!px-6 !py-2 !text-base ${!isEditCustomPath && !isPreviewAvailable
                    ? '!bg-gray-300 !text-gray-500 opacity-40 cursor-not-allowed'
                    : ''
                    }`}
                />
              </>
            )}
          </div>
          {localAppVersion && (
            <p
              className={
                useCompactModal
                  ? settingsPortraitCompact
                    ? 'shrink-0 px-4 py-1 text-center text-[10px] text-gray-400'
                    : 'shrink-0 px-3 py-1 text-center text-xs text-gray-400'
                  : 'absolute bottom-2 left-8 mt-2 text-sm text-gray-300'
              }
            >
              {t('settings.appVersion.label')} {localAppVersion}
            </p>
          )}
          {ENABLE_UPDATE_NOTIFICATIONS && showUpdateBubble && latestAppVersion && (
            <div
              className={`absolute z-[210] flex cursor-pointer items-center gap-2 rounded-full bg-blue-500 p-2 text-sm font-bold text-white shadow-lg animate-bounce ${useCompactModal ? 'right-4 top-16 sm:right-12 sm:top-24' : 'right-12 top-24'
                }`}
              onClick={() =>
                void openExternalUrl(`${process.env.VITE_API_URL}/dashboard/installation`)
              }
            >
              {t('settings.appVersion.updateAvailable')} {latestAppVersion}{' '}
              <FaExternalLinkAlt size={14} />
            </div>
          )}
        </div>
      )}

      <CameraGuideModal
        isOpen={showGuideModal}
        onClose={() => setShowGuideModal(false)}
        isElectron={hasElectronPreload()}
        onFetchDevices={fetchDevices}
      />

      <PrinterGuideModal
        isOpen={showPrinterGuideModal}
        onClose={() => setShowPrinterGuideModal(false)}
      />

      <PrinterSettingsModal
        isOpen={showPrinterSettings}
        onClose={() => setShowPrinterSettings(false)}
        isElectron={hasElectronPreload()}
      />

      <RemotePrintSettingsModal
        isOpen={showRemotePrintSettings}
        onClose={() => setShowRemotePrintSettings(false)}
      />

      <CameraSettingsModal
        isOpen={showCameraSettings}
        onClose={() => {
          setShowCameraSettings(false)
          setIsPreviewAvailable(true)
          // The sub-modal can change the PRIMARY selection too now — resync this
          // modal's summary state so it doesn't show a stale camera label.
          setLocalTempCamera(useSettingsStore.getState().selectedCamera || '')
        }}
      />

      <PinModal
        isOpen={showChangePinModal}
        onClose={() => setShowChangePinModal(false)}
        onSubmit={handlePinChange}
        title={pinModalTitle}
        subtitle={pinModalSubtitle}
      />

      {showUpgradeModal && <UpgradeModal onClose={() => setShowUpgradeModal(false)} />}

      <DiskCleanupModal
        isOpen={showDiskCleanup}
        onClose={() => setShowDiskCleanup(false)}
        onCleaned={refreshDiskStatus}
      />
    </div>
  )
}

export default SettingsModal
